import { createAction, createSelector, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SignedUrl, assert, getFileExtension, compareDateTimes, includes } from '@faro-lotv/foundation';
import { useState, useEffect, useCallback } from 'react';
import { makeIElementUriSigner } from '@faro-lotv/service-wires';
import { LabelType, IElementTypeHint, IElementType, isValid, isIElementProjectRoot, isIElementPanoramaImage, validateKnownIElementTypes, isIElementSectionDataSession, isIElementImg360, pickClosestInTime, areCreatedOnSameDate, isValidPose, isIElementAreaSection, isIElementModel3d, isIElementSection, isIElementTimeseries, isIElementVideoRecording, isIElementSectionWithTypeHint, isIElementGenericDataset, isIElementDataSetVideoWalk, isIElementBimModelSection, isIElementRoomSection, isIElementExternalMarkup, isIElementGenericAnnotation, isIElementPanoInOdometryPath, isIElementGenericPointCloudStream, isIElementSectionGeoslam, isCaptureTreeRoot, isIElementTimeseriesDataSession, isIElementPointCloudStream, isIElementGaussianSplats, isIElementGenericImgSheet, isIElementVideoRecordingTimeseries, isIElementWithTypeAndHint, isIElementImg2d, isIElementVolume, isIElementMarkupAssigneeIdTemplate, isIElementMarkupIssueStatusTemplate, isIElementMarkupIssueDueDateTemplate, isIElementCamView, isIElementBimModelGroup, isIElementGroup, isIElemLink, isIElementWithTypeAndHints } from '@faro-lotv/ielement-types';

/**
 * The action to clear the store
 * Exported here, so that the same action can be used in client applications
 * thereby, removing the duplication of clearStore action and also having a same name for the action
 */
const clearStore = createAction("clearStore");

/**
 * Request an updated and valid signed url for a project resource.
 * When the signed URL is required immediately by a component use useSignedUrl hook
 *
 * @param projectApi client class to request new signatures
 * @param uri the uri to sign
 * @param elementId the id of the element that contains this resource
 * @param initialSignature the initial signature for this uri if available
 * @returns a valid signed url for the requested resource if available
 */
async function requestSignedUrlForIElement(projectApi, uri, elementId, initialSignature) {
    return await new SignedUrl(uri, makeIElementUriSigner(projectApi, elementId), initialSignature)
        .requestSignedUrl()
        .catch((reason) => (reason instanceof Error ? reason : new Error(reason)));
}
/**
 * @returns a SignedUrl instance for the `uri` field of an `IElementWithFileUri`.
 * @param projectApi The project api instance to use for signing
 * @param element The element to create a SignedUrl for
 */
function createSignedUrlForIElementUri(projectApi, element) {
    return new SignedUrl(element.uri, makeIElementUriSigner(projectApi, element.id), element.signedUri && element.signedUriExpiresOn
        ? {
            signedUrl: element.signedUri,
            expiresOn: new Date(element.signedUriExpiresOn),
        }
        : undefined);
}
/**
 * @returns a SignedUrl instance for the `thumbnailUri` field of an IElement. Or undefined if the element has no thumbnail.
 * @param projectApi The project api instance to use for signing
 * @param element The element to create a SignedUrl for
 */
function createSignedUrlForIElementThumbnailUri(projectApi, element) {
    if (!element.thumbnailUri)
        return;
    return new SignedUrl(element.thumbnailUri, makeIElementUriSigner(projectApi, element.id), element.signedThumbnailUri && element.signedThumbnailUriExpiresOn
        ? {
            signedUrl: element.signedThumbnailUri,
            expiresOn: new Date(element.signedThumbnailUriExpiresOn),
        }
        : undefined);
}
/**
 * @returns Signed URL instances for the tiles of an `IElementImg360`.
 * Returns a double nested array that is accessed [levelIndex][sourceIndex] corresponding to the input data.
 * @param projectApi The ProjectApi instance to use for signing
 * @param element The `IElementImg360` to create SignedUrls for
 */
function createSignedUrlsForIElementLevelsOfDetail(projectApi, element) {
    return element.levelsOfDetail?.map((level) => createSignedUrlsForIElementSources(projectApi, element.id, level));
}
/**
 * @returns Signed URL instances for the sources of a SourcesData compatible object. (e.g. `Img360LevelsOfDetail`)
 * @param projectApi The ProjectApi instance to use for signing
 * @param elementId The id of the element that contains these sources
 * @param sourcesData The SourcesData to create SignedUrls for
 */
function createSignedUrlsForIElementSources(projectApi, elementId, sourcesData) {
    return sourcesData.sources.map((source, index) => {
        const signedSource = sourcesData.signedSources?.[index];
        return new SignedUrl(source, makeIElementUriSigner(projectApi, elementId), signedSource && sourcesData.signedSourcesExpiresOn
            ? {
                signedUrl: signedSource,
                expiresOn: new Date(sourcesData.signedSourcesExpiresOn),
            }
            : undefined);
    });
}

/**
 * Request an updated and valid signed url for a project resource.
 * Useful when the signed url of a resources is required immediately by a component
 * Eg: to load a floor image texture
 *
 * NOTE: This hook will not re-request an updated signature when the first one expires
 * as it expect the resource to be immediately fetched and cached for the current component lifetime
 *
 * If the component may need to request the resources at a later time you can use {@link requestSignedUrlForIElement}
 *
 * @param projectApi client class to request new signatures
 * @param uri the uri to sign
 * @param elementId the id of the element that contains this resource
 * @param initialSignature the initial signature for this uri if available
 * @returns a valid signed url for the requested resource if available, if not undefined
 */
function useSignedUrl(projectApi, uri, elementId, initialSignature) {
    const [signedUrl, setSignedUrl] = useState();
    useEffect(() => {
        requestSignedUrlForIElement(projectApi, uri, elementId, initialSignature).then((resultSignedUrl) => {
            setSignedUrl(resultSignedUrl);
        });
    }, [uri, elementId, initialSignature, projectApi]);
    return signedUrl;
}
/**
 * A signed URL which is refreshed when it expires.
 *
 * @param signedUrl The signed url to validate
 * @returns A valid signed URL string.
 */
function useValidSignedUrl(signedUrl) {
    const [validUrl, setValidUrl] = useState(signedUrl?.isValid ? signedUrl.signedUrl : undefined);
    // Refresh the URL when it expired
    useEffect(() => {
        if (!signedUrl || signedUrl.isValid) {
            setValidUrl(signedUrl?.signedUrl);
            return;
        }
        signedUrl.requestSignedUrl().then((newUrl) => {
            setValidUrl(newUrl);
        });
    }, [signedUrl]);
    // Listen for external updates to the signed URL
    useEffect(() => {
        if (!signedUrl) {
            return;
        }
        function onUrlUpdated(data) {
            setValidUrl(data.signedUrl);
        }
        signedUrl.urlUpdated.on(onUrlUpdated);
        return () => {
            signedUrl.urlUpdated.off(onUrlUpdated);
        };
    }, [signedUrl]);
    return validUrl;
}
/**
 * A list of signed URLs which are refreshed initially, and when updated externally.
 *
 * @param signedUrls The signed urls to validate
 * @returns The valid signed URL strings.
 */
function useValidSignedUrls(signedUrls) {
    const [validUrls, setValidUrls] = useState(() => signedUrls.map((signedUrl) => signedUrl.isValid ? signedUrl.signedUrl : undefined));
    const updateUrlAtIndex = useCallback((index, newUrl) => setValidUrls((prevUrls) => [
        ...prevUrls.slice(0, index),
        newUrl,
        ...prevUrls.slice(index + 1),
    ]), []);
    // Refresh the URLs when they are expired
    useEffect(() => {
        const urls = [];
        for (const [index, signedUrl] of signedUrls.entries()) {
            urls.push(signedUrl.signedUrl);
            if (signedUrl.isValid) {
                continue;
            }
            signedUrl.requestSignedUrl().then((newUrl) => {
                updateUrlAtIndex(index, newUrl);
            });
        }
        // In case the input list signedUrls contains a list of valid urls
        // but of different elements, be sure to keep it in sync
        setValidUrls(urls);
    }, [signedUrls, updateUrlAtIndex]);
    // Listen for external updates to the signed URLs
    useEffect(() => {
        const cleanups = [];
        for (const [index, signedUrl] of signedUrls.entries()) {
            function onUrlUpdated(data) {
                updateUrlAtIndex(index, data.signedUrl);
            }
            signedUrl.urlUpdated.on(onUrlUpdated);
            cleanups.push(() => signedUrl.urlUpdated.off(onUrlUpdated));
        }
        return () => {
            for (const cleanup of cleanups) {
                cleanup();
            }
        };
    }, [signedUrls, updateUrlAtIndex]);
    return validUrls;
}
/**
 * @param url The URL to validate.
 * @returns If the URL is a string, it's returned as-is.
 *  If the URL is a {@link SignedUrl}, it's validated and returned as a string.
 */
function useValidUrl(url) {
    const signedUrl = useValidSignedUrl(url instanceof SignedUrl ? url : undefined);
    return typeof url === "string" ? url : signedUrl;
}
/**
 * Utility hook to keep a set of signed URLs valid as long as the hook is mounted.
 * Should be used only when really needed, as it will continuously trigger network requests.
 * When the signing fails, the current implementation will stop trying to refresh the URL.
 *
 * @param signedUrls The signed URLs to keep alive.
 * @param enabled Whether the logic to keep the URLs alive should be enabled. @default true.
 */
function useKeepSignedUrlsAlive(signedUrls, enabled = true) {
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const updateTimeouts = signedUrls.map((signedUrl, index) => {
            async function updateUrl() {
                updateTimeouts[index] = undefined;
                try {
                    await signedUrl.requestSignedUrl();
                    updateTimeouts[index] = scheduleUpdateTimeout();
                }
                catch {
                    // Do not schedule further updates, if the signing process fails.
                    // This is needed to avoid infinite loops with un-signable URLs.
                    // One drawback is that network errors will not be retried, but the un-signable URLs should be a lot more common.
                }
            }
            function scheduleUpdateTimeout() {
                return setTimeout(updateUrl, Math.max(1000, signedUrl.timeUntilExpiredMs));
            }
            return scheduleUpdateTimeout();
        });
        return () => {
            for (const updateTimeout of updateTimeouts) {
                if (updateTimeout !== undefined) {
                    clearTimeout(updateTimeout);
                }
            }
        };
    }, [signedUrls, enabled]);
}

function partition(arr, isInTruthy) {
    const truthy = [];
    const falsy = [];
    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        if (isInTruthy(item)) {
            truthy.push(item);
        }
        else {
            falsy.push(item);
        }
    }
    return [truthy, falsy];
}

/**
 * @returns True if the iElement was obtained by copying data from a video mode path
 * @param iElement The element to check
 */
function isIElementVideoModeCopy(iElement) {
    return (!!iElement.labels &&
        iElement.labels.some(({ labelType }) => labelType === LabelType.Videomode));
}

const WebGLCoordinateSystem = 2000;
const WebGPUCoordinateSystem = 2001;

function clamp( value, min, max ) {

	return Math.max( min, Math.min( max, value ) );

}

class Quaternion {

	constructor( x = 0, y = 0, z = 0, w = 1 ) {

		this.isQuaternion = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

	}

	static slerpFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1, t ) {

		// fuzz-free, array-based Quaternion SLERP operation

		let x0 = src0[ srcOffset0 + 0 ],
			y0 = src0[ srcOffset0 + 1 ],
			z0 = src0[ srcOffset0 + 2 ],
			w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 + 0 ],
			y1 = src1[ srcOffset1 + 1 ],
			z1 = src1[ srcOffset1 + 2 ],
			w1 = src1[ srcOffset1 + 3 ];

		if ( t === 0 ) {

			dst[ dstOffset + 0 ] = x0;
			dst[ dstOffset + 1 ] = y0;
			dst[ dstOffset + 2 ] = z0;
			dst[ dstOffset + 3 ] = w0;
			return;

		}

		if ( t === 1 ) {

			dst[ dstOffset + 0 ] = x1;
			dst[ dstOffset + 1 ] = y1;
			dst[ dstOffset + 2 ] = z1;
			dst[ dstOffset + 3 ] = w1;
			return;

		}

		if ( w0 !== w1 || x0 !== x1 || y0 !== y1 || z0 !== z1 ) {

			let s = 1 - t;
			const cos = x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1,
				dir = ( cos >= 0 ? 1 : - 1 ),
				sqrSin = 1 - cos * cos;

			// Skip the Slerp for tiny steps to avoid numeric problems:
			if ( sqrSin > Number.EPSILON ) {

				const sin = Math.sqrt( sqrSin ),
					len = Math.atan2( sin, cos * dir );

				s = Math.sin( s * len ) / sin;
				t = Math.sin( t * len ) / sin;

			}

			const tDir = t * dir;

			x0 = x0 * s + x1 * tDir;
			y0 = y0 * s + y1 * tDir;
			z0 = z0 * s + z1 * tDir;
			w0 = w0 * s + w1 * tDir;

			// Normalize in case we just did a lerp:
			if ( s === 1 - t ) {

				const f = 1 / Math.sqrt( x0 * x0 + y0 * y0 + z0 * z0 + w0 * w0 );

				x0 *= f;
				y0 *= f;
				z0 *= f;
				w0 *= f;

			}

		}

		dst[ dstOffset ] = x0;
		dst[ dstOffset + 1 ] = y0;
		dst[ dstOffset + 2 ] = z0;
		dst[ dstOffset + 3 ] = w0;

	}

	static multiplyQuaternionsFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1 ) {

		const x0 = src0[ srcOffset0 ];
		const y0 = src0[ srcOffset0 + 1 ];
		const z0 = src0[ srcOffset0 + 2 ];
		const w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 ];
		const y1 = src1[ srcOffset1 + 1 ];
		const z1 = src1[ srcOffset1 + 2 ];
		const w1 = src1[ srcOffset1 + 3 ];

		dst[ dstOffset ] = x0 * w1 + w0 * x1 + y0 * z1 - z0 * y1;
		dst[ dstOffset + 1 ] = y0 * w1 + w0 * y1 + z0 * x1 - x0 * z1;
		dst[ dstOffset + 2 ] = z0 * w1 + w0 * z1 + x0 * y1 - y0 * x1;
		dst[ dstOffset + 3 ] = w0 * w1 - x0 * x1 - y0 * y1 - z0 * z1;

		return dst;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get w() {

		return this._w;

	}

	set w( value ) {

		this._w = value;
		this._onChangeCallback();

	}

	set( x, y, z, w ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._w );

	}

	copy( quaternion ) {

		this._x = quaternion.x;
		this._y = quaternion.y;
		this._z = quaternion.z;
		this._w = quaternion.w;

		this._onChangeCallback();

		return this;

	}

	setFromEuler( euler, update ) {

		const x = euler._x, y = euler._y, z = euler._z, order = euler._order;

		// http://www.mathworks.com/matlabcentral/fileexchange/
		// 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
		//	content/SpinCalc.m

		const cos = Math.cos;
		const sin = Math.sin;

		const c1 = cos( x / 2 );
		const c2 = cos( y / 2 );
		const c3 = cos( z / 2 );

		const s1 = sin( x / 2 );
		const s2 = sin( y / 2 );
		const s3 = sin( z / 2 );

		switch ( order ) {

			case 'XYZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'YXZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'ZXY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'ZYX':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'YZX':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'XZY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			default:
				console.warn( 'THREE.Quaternion: .setFromEuler() encountered an unknown order: ' + order );

		}

		if ( update !== false ) this._onChangeCallback();

		return this;

	}

	setFromAxisAngle( axis, angle ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm

		// assumes axis is normalized

		const halfAngle = angle / 2, s = Math.sin( halfAngle );

		this._x = axis.x * s;
		this._y = axis.y * s;
		this._z = axis.z * s;
		this._w = Math.cos( halfAngle );

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements,

			m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ],
			m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ],
			m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ],

			trace = m11 + m22 + m33;

		if ( trace > 0 ) {

			const s = 0.5 / Math.sqrt( trace + 1.0 );

			this._w = 0.25 / s;
			this._x = ( m32 - m23 ) * s;
			this._y = ( m13 - m31 ) * s;
			this._z = ( m21 - m12 ) * s;

		} else if ( m11 > m22 && m11 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m11 - m22 - m33 );

			this._w = ( m32 - m23 ) / s;
			this._x = 0.25 * s;
			this._y = ( m12 + m21 ) / s;
			this._z = ( m13 + m31 ) / s;

		} else if ( m22 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m22 - m11 - m33 );

			this._w = ( m13 - m31 ) / s;
			this._x = ( m12 + m21 ) / s;
			this._y = 0.25 * s;
			this._z = ( m23 + m32 ) / s;

		} else {

			const s = 2.0 * Math.sqrt( 1.0 + m33 - m11 - m22 );

			this._w = ( m21 - m12 ) / s;
			this._x = ( m13 + m31 ) / s;
			this._y = ( m23 + m32 ) / s;
			this._z = 0.25 * s;

		}

		this._onChangeCallback();

		return this;

	}

	setFromUnitVectors( vFrom, vTo ) {

		// assumes direction vectors vFrom and vTo are normalized

		let r = vFrom.dot( vTo ) + 1;

		if ( r < Number.EPSILON ) {

			// vFrom and vTo point in opposite directions

			r = 0;

			if ( Math.abs( vFrom.x ) > Math.abs( vFrom.z ) ) {

				this._x = - vFrom.y;
				this._y = vFrom.x;
				this._z = 0;
				this._w = r;

			} else {

				this._x = 0;
				this._y = - vFrom.z;
				this._z = vFrom.y;
				this._w = r;

			}

		} else {

			// crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3

			this._x = vFrom.y * vTo.z - vFrom.z * vTo.y;
			this._y = vFrom.z * vTo.x - vFrom.x * vTo.z;
			this._z = vFrom.x * vTo.y - vFrom.y * vTo.x;
			this._w = r;

		}

		return this.normalize();

	}

	angleTo( q ) {

		return 2 * Math.acos( Math.abs( clamp( this.dot( q ), - 1, 1 ) ) );

	}

	rotateTowards( q, step ) {

		const angle = this.angleTo( q );

		if ( angle === 0 ) return this;

		const t = Math.min( 1, step / angle );

		this.slerp( q, t );

		return this;

	}

	identity() {

		return this.set( 0, 0, 0, 1 );

	}

	invert() {

		// quaternion is assumed to have unit length

		return this.conjugate();

	}

	conjugate() {

		this._x *= - 1;
		this._y *= - 1;
		this._z *= - 1;

		this._onChangeCallback();

		return this;

	}

	dot( v ) {

		return this._x * v._x + this._y * v._y + this._z * v._z + this._w * v._w;

	}

	lengthSq() {

		return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;

	}

	length() {

		return Math.sqrt( this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w );

	}

	normalize() {

		let l = this.length();

		if ( l === 0 ) {

			this._x = 0;
			this._y = 0;
			this._z = 0;
			this._w = 1;

		} else {

			l = 1 / l;

			this._x = this._x * l;
			this._y = this._y * l;
			this._z = this._z * l;
			this._w = this._w * l;

		}

		this._onChangeCallback();

		return this;

	}

	multiply( q ) {

		return this.multiplyQuaternions( this, q );

	}

	premultiply( q ) {

		return this.multiplyQuaternions( q, this );

	}

	multiplyQuaternions( a, b ) {

		// from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm

		const qax = a._x, qay = a._y, qaz = a._z, qaw = a._w;
		const qbx = b._x, qby = b._y, qbz = b._z, qbw = b._w;

		this._x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby;
		this._y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz;
		this._z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx;
		this._w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz;

		this._onChangeCallback();

		return this;

	}

	slerp( qb, t ) {

		if ( t === 0 ) return this;
		if ( t === 1 ) return this.copy( qb );

		const x = this._x, y = this._y, z = this._z, w = this._w;

		// http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/

		let cosHalfTheta = w * qb._w + x * qb._x + y * qb._y + z * qb._z;

		if ( cosHalfTheta < 0 ) {

			this._w = - qb._w;
			this._x = - qb._x;
			this._y = - qb._y;
			this._z = - qb._z;

			cosHalfTheta = - cosHalfTheta;

		} else {

			this.copy( qb );

		}

		if ( cosHalfTheta >= 1.0 ) {

			this._w = w;
			this._x = x;
			this._y = y;
			this._z = z;

			return this;

		}

		const sqrSinHalfTheta = 1.0 - cosHalfTheta * cosHalfTheta;

		if ( sqrSinHalfTheta <= Number.EPSILON ) {

			const s = 1 - t;
			this._w = s * w + t * this._w;
			this._x = s * x + t * this._x;
			this._y = s * y + t * this._y;
			this._z = s * z + t * this._z;

			this.normalize();
			this._onChangeCallback();

			return this;

		}

		const sinHalfTheta = Math.sqrt( sqrSinHalfTheta );
		const halfTheta = Math.atan2( sinHalfTheta, cosHalfTheta );
		const ratioA = Math.sin( ( 1 - t ) * halfTheta ) / sinHalfTheta,
			ratioB = Math.sin( t * halfTheta ) / sinHalfTheta;

		this._w = ( w * ratioA + this._w * ratioB );
		this._x = ( x * ratioA + this._x * ratioB );
		this._y = ( y * ratioA + this._y * ratioB );
		this._z = ( z * ratioA + this._z * ratioB );

		this._onChangeCallback();

		return this;

	}

	slerpQuaternions( qa, qb, t ) {

		return this.copy( qa ).slerp( qb, t );

	}

	random() {

		// Derived from http://planning.cs.uiuc.edu/node198.html
		// Note, this source uses w, x, y, z ordering,
		// so we swap the order below.

		const u1 = Math.random();
		const sqrt1u1 = Math.sqrt( 1 - u1 );
		const sqrtu1 = Math.sqrt( u1 );

		const u2 = 2 * Math.PI * Math.random();

		const u3 = 2 * Math.PI * Math.random();

		return this.set(
			sqrt1u1 * Math.cos( u2 ),
			sqrtu1 * Math.sin( u3 ),
			sqrtu1 * Math.cos( u3 ),
			sqrt1u1 * Math.sin( u2 ),
		);

	}

	equals( quaternion ) {

		return ( quaternion._x === this._x ) && ( quaternion._y === this._y ) && ( quaternion._z === this._z ) && ( quaternion._w === this._w );

	}

	fromArray( array, offset = 0 ) {

		this._x = array[ offset ];
		this._y = array[ offset + 1 ];
		this._z = array[ offset + 2 ];
		this._w = array[ offset + 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._w;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this._x = attribute.getX( index );
		this._y = attribute.getY( index );
		this._z = attribute.getZ( index );
		this._w = attribute.getW( index );

		return this;

	}

	toJSON() {

		return this.toArray();

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._w;

	}

}

class Vector3 {

	constructor( x = 0, y = 0, z = 0 ) {

		Vector3.prototype.isVector3 = true;

		this.x = x;
		this.y = y;
		this.z = z;

	}

	set( x, y, z ) {

		if ( z === undefined ) z = this.z; // sprite.scale.set(x,y)

		this.x = x;
		this.y = y;
		this.z = z;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;
		this.z = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setZ( z ) {

		this.z = z;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			case 2: this.z = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			case 2: return this.z;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y, this.z );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;
		this.z = v.z;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;
		this.z += v.z;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;
		this.z += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;
		this.z = a.z + b.z;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;
		this.z += v.z * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;
		this.z -= v.z;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;
		this.z -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;
		this.z = a.z - b.z;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;
		this.z *= v.z;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;
		this.z *= scalar;

		return this;

	}

	multiplyVectors( a, b ) {

		this.x = a.x * b.x;
		this.y = a.y * b.y;
		this.z = a.z * b.z;

		return this;

	}

	applyEuler( euler ) {

		return this.applyQuaternion( _quaternion.setFromEuler( euler ) );

	}

	applyAxisAngle( axis, angle ) {

		return this.applyQuaternion( _quaternion.setFromAxisAngle( axis, angle ) );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ] * z;
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ] * z;
		this.z = e[ 2 ] * x + e[ 5 ] * y + e[ 8 ] * z;

		return this;

	}

	applyNormalMatrix( m ) {

		return this.applyMatrix3( m ).normalize();

	}

	applyMatrix4( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		const w = 1 / ( e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] );

		this.x = ( e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] ) * w;
		this.y = ( e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] ) * w;
		this.z = ( e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] ) * w;

		return this;

	}

	applyQuaternion( q ) {

		// quaternion q is assumed to have unit length

		const vx = this.x, vy = this.y, vz = this.z;
		const qx = q.x, qy = q.y, qz = q.z, qw = q.w;

		// t = 2 * cross( q.xyz, v );
		const tx = 2 * ( qy * vz - qz * vy );
		const ty = 2 * ( qz * vx - qx * vz );
		const tz = 2 * ( qx * vy - qy * vx );

		// v + q.w * t + cross( q.xyz, t );
		this.x = vx + qw * tx + qy * tz - qz * ty;
		this.y = vy + qw * ty + qz * tx - qx * tz;
		this.z = vz + qw * tz + qx * ty - qy * tx;

		return this;

	}

	project( camera ) {

		return this.applyMatrix4( camera.matrixWorldInverse ).applyMatrix4( camera.projectionMatrix );

	}

	unproject( camera ) {

		return this.applyMatrix4( camera.projectionMatrixInverse ).applyMatrix4( camera.matrixWorld );

	}

	transformDirection( m ) {

		// input: THREE.Matrix4 affine matrix
		// vector interpreted as a direction

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z;
		this.y = e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z;
		this.z = e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z;

		return this.normalize();

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;
		this.z /= v.z;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );
		this.z = Math.min( this.z, v.z );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );
		this.z = Math.max( this.z, v.z );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );
		this.z = Math.max( min.z, Math.min( max.z, this.z ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );
		this.z = Math.max( minVal, Math.min( maxVal, this.z ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );
		this.z = Math.floor( this.z );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );
		this.z = Math.ceil( this.z );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );
		this.z = Math.round( this.z );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );
		this.z = Math.trunc( this.z );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;
		this.z = - this.z;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y + this.z * v.z;

	}

	// TODO lengthSquared?

	lengthSq() {

		return this.x * this.x + this.y * this.y + this.z * this.z;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y + this.z * this.z );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y ) + Math.abs( this.z );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;
		this.z += ( v.z - this.z ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;
		this.z = v1.z + ( v2.z - v1.z ) * alpha;

		return this;

	}

	cross( v ) {

		return this.crossVectors( this, v );

	}

	crossVectors( a, b ) {

		const ax = a.x, ay = a.y, az = a.z;
		const bx = b.x, by = b.y, bz = b.z;

		this.x = ay * bz - az * by;
		this.y = az * bx - ax * bz;
		this.z = ax * by - ay * bx;

		return this;

	}

	projectOnVector( v ) {

		const denominator = v.lengthSq();

		if ( denominator === 0 ) return this.set( 0, 0, 0 );

		const scalar = v.dot( this ) / denominator;

		return this.copy( v ).multiplyScalar( scalar );

	}

	projectOnPlane( planeNormal ) {

		_vector.copy( this ).projectOnVector( planeNormal );

		return this.sub( _vector );

	}

	reflect( normal ) {

		// reflect incident vector off plane orthogonal to normal
		// normal is assumed to have unit length

		return this.sub( _vector.copy( normal ).multiplyScalar( 2 * this.dot( normal ) ) );

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;

		return dx * dx + dy * dy + dz * dz;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y ) + Math.abs( this.z - v.z );

	}

	setFromSpherical( s ) {

		return this.setFromSphericalCoords( s.radius, s.phi, s.theta );

	}

	setFromSphericalCoords( radius, phi, theta ) {

		const sinPhiRadius = Math.sin( phi ) * radius;

		this.x = sinPhiRadius * Math.sin( theta );
		this.y = Math.cos( phi ) * radius;
		this.z = sinPhiRadius * Math.cos( theta );

		return this;

	}

	setFromCylindrical( c ) {

		return this.setFromCylindricalCoords( c.radius, c.theta, c.y );

	}

	setFromCylindricalCoords( radius, theta, y ) {

		this.x = radius * Math.sin( theta );
		this.y = y;
		this.z = radius * Math.cos( theta );

		return this;

	}

	setFromMatrixPosition( m ) {

		const e = m.elements;

		this.x = e[ 12 ];
		this.y = e[ 13 ];
		this.z = e[ 14 ];

		return this;

	}

	setFromMatrixScale( m ) {

		const sx = this.setFromMatrixColumn( m, 0 ).length();
		const sy = this.setFromMatrixColumn( m, 1 ).length();
		const sz = this.setFromMatrixColumn( m, 2 ).length();

		this.x = sx;
		this.y = sy;
		this.z = sz;

		return this;

	}

	setFromMatrixColumn( m, index ) {

		return this.fromArray( m.elements, index * 4 );

	}

	setFromMatrix3Column( m, index ) {

		return this.fromArray( m.elements, index * 3 );

	}

	setFromEuler( e ) {

		this.x = e._x;
		this.y = e._y;
		this.z = e._z;

		return this;

	}

	setFromColor( c ) {

		this.x = c.r;
		this.y = c.g;
		this.z = c.b;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) && ( v.z === this.z ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];
		this.z = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;
		array[ offset + 2 ] = this.z;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );
		this.z = attribute.getZ( index );

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();
		this.z = Math.random();

		return this;

	}

	randomDirection() {

		// Derived from https://mathworld.wolfram.com/SpherePointPicking.html

		const u = ( Math.random() - 0.5 ) * 2;
		const t = Math.random() * Math.PI * 2;
		const f = Math.sqrt( 1 - u ** 2 );

		this.x = f * Math.cos( t );
		this.y = f * Math.sin( t );
		this.z = u;

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;
		yield this.z;

	}

}

const _vector = /*@__PURE__*/ new Vector3();
const _quaternion = /*@__PURE__*/ new Quaternion();

class Matrix4 {

	constructor( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		Matrix4.prototype.isMatrix4 = true;

		this.elements = [

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 );

		}

	}

	set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 4 ] = n12; te[ 8 ] = n13; te[ 12 ] = n14;
		te[ 1 ] = n21; te[ 5 ] = n22; te[ 9 ] = n23; te[ 13 ] = n24;
		te[ 2 ] = n31; te[ 6 ] = n32; te[ 10 ] = n33; te[ 14 ] = n34;
		te[ 3 ] = n41; te[ 7 ] = n42; te[ 11 ] = n43; te[ 15 ] = n44;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	clone() {

		return new Matrix4().fromArray( this.elements );

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ]; te[ 3 ] = me[ 3 ];
		te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ]; te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ];
		te[ 8 ] = me[ 8 ]; te[ 9 ] = me[ 9 ]; te[ 10 ] = me[ 10 ]; te[ 11 ] = me[ 11 ];
		te[ 12 ] = me[ 12 ]; te[ 13 ] = me[ 13 ]; te[ 14 ] = me[ 14 ]; te[ 15 ] = me[ 15 ];

		return this;

	}

	copyPosition( m ) {

		const te = this.elements, me = m.elements;

		te[ 12 ] = me[ 12 ];
		te[ 13 ] = me[ 13 ];
		te[ 14 ] = me[ 14 ];

		return this;

	}

	setFromMatrix3( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 3 ], me[ 6 ], 0,
			me[ 1 ], me[ 4 ], me[ 7 ], 0,
			me[ 2 ], me[ 5 ], me[ 8 ], 0,
			0, 0, 0, 1

		);

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrixColumn( this, 0 );
		yAxis.setFromMatrixColumn( this, 1 );
		zAxis.setFromMatrixColumn( this, 2 );

		return this;

	}

	makeBasis( xAxis, yAxis, zAxis ) {

		this.set(
			xAxis.x, yAxis.x, zAxis.x, 0,
			xAxis.y, yAxis.y, zAxis.y, 0,
			xAxis.z, yAxis.z, zAxis.z, 0,
			0, 0, 0, 1
		);

		return this;

	}

	extractRotation( m ) {

		// this method does not support reflection matrices

		const te = this.elements;
		const me = m.elements;

		const scaleX = 1 / _v1.setFromMatrixColumn( m, 0 ).length();
		const scaleY = 1 / _v1.setFromMatrixColumn( m, 1 ).length();
		const scaleZ = 1 / _v1.setFromMatrixColumn( m, 2 ).length();

		te[ 0 ] = me[ 0 ] * scaleX;
		te[ 1 ] = me[ 1 ] * scaleX;
		te[ 2 ] = me[ 2 ] * scaleX;
		te[ 3 ] = 0;

		te[ 4 ] = me[ 4 ] * scaleY;
		te[ 5 ] = me[ 5 ] * scaleY;
		te[ 6 ] = me[ 6 ] * scaleY;
		te[ 7 ] = 0;

		te[ 8 ] = me[ 8 ] * scaleZ;
		te[ 9 ] = me[ 9 ] * scaleZ;
		te[ 10 ] = me[ 10 ] * scaleZ;
		te[ 11 ] = 0;

		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromEuler( euler ) {

		const te = this.elements;

		const x = euler.x, y = euler.y, z = euler.z;
		const a = Math.cos( x ), b = Math.sin( x );
		const c = Math.cos( y ), d = Math.sin( y );
		const e = Math.cos( z ), f = Math.sin( z );

		if ( euler.order === 'XYZ' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = - c * f;
			te[ 8 ] = d;

			te[ 1 ] = af + be * d;
			te[ 5 ] = ae - bf * d;
			te[ 9 ] = - b * c;

			te[ 2 ] = bf - ae * d;
			te[ 6 ] = be + af * d;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YXZ' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce + df * b;
			te[ 4 ] = de * b - cf;
			te[ 8 ] = a * d;

			te[ 1 ] = a * f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b;

			te[ 2 ] = cf * b - de;
			te[ 6 ] = df + ce * b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZXY' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce - df * b;
			te[ 4 ] = - a * f;
			te[ 8 ] = de + cf * b;

			te[ 1 ] = cf + de * b;
			te[ 5 ] = a * e;
			te[ 9 ] = df - ce * b;

			te[ 2 ] = - a * d;
			te[ 6 ] = b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZYX' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = be * d - af;
			te[ 8 ] = ae * d + bf;

			te[ 1 ] = c * f;
			te[ 5 ] = bf * d + ae;
			te[ 9 ] = af * d - be;

			te[ 2 ] = - d;
			te[ 6 ] = b * c;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YZX' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = bd - ac * f;
			te[ 8 ] = bc * f + ad;

			te[ 1 ] = f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b * e;

			te[ 2 ] = - d * e;
			te[ 6 ] = ad * f + bc;
			te[ 10 ] = ac - bd * f;

		} else if ( euler.order === 'XZY' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = - f;
			te[ 8 ] = d * e;

			te[ 1 ] = ac * f + bd;
			te[ 5 ] = a * e;
			te[ 9 ] = ad * f - bc;

			te[ 2 ] = bc * f - ad;
			te[ 6 ] = b * e;
			te[ 10 ] = bd * f + ac;

		}

		// bottom row
		te[ 3 ] = 0;
		te[ 7 ] = 0;
		te[ 11 ] = 0;

		// last column
		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromQuaternion( q ) {

		return this.compose( _zero, q, _one );

	}

	lookAt( eye, target, up ) {

		const te = this.elements;

		_z.subVectors( eye, target );

		if ( _z.lengthSq() === 0 ) {

			// eye and target are in the same position

			_z.z = 1;

		}

		_z.normalize();
		_x.crossVectors( up, _z );

		if ( _x.lengthSq() === 0 ) {

			// up and z are parallel

			if ( Math.abs( up.z ) === 1 ) {

				_z.x += 0.0001;

			} else {

				_z.z += 0.0001;

			}

			_z.normalize();
			_x.crossVectors( up, _z );

		}

		_x.normalize();
		_y.crossVectors( _z, _x );

		te[ 0 ] = _x.x; te[ 4 ] = _y.x; te[ 8 ] = _z.x;
		te[ 1 ] = _x.y; te[ 5 ] = _y.y; te[ 9 ] = _z.y;
		te[ 2 ] = _x.z; te[ 6 ] = _y.z; te[ 10 ] = _z.z;

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 4 ], a13 = ae[ 8 ], a14 = ae[ 12 ];
		const a21 = ae[ 1 ], a22 = ae[ 5 ], a23 = ae[ 9 ], a24 = ae[ 13 ];
		const a31 = ae[ 2 ], a32 = ae[ 6 ], a33 = ae[ 10 ], a34 = ae[ 14 ];
		const a41 = ae[ 3 ], a42 = ae[ 7 ], a43 = ae[ 11 ], a44 = ae[ 15 ];

		const b11 = be[ 0 ], b12 = be[ 4 ], b13 = be[ 8 ], b14 = be[ 12 ];
		const b21 = be[ 1 ], b22 = be[ 5 ], b23 = be[ 9 ], b24 = be[ 13 ];
		const b31 = be[ 2 ], b32 = be[ 6 ], b33 = be[ 10 ], b34 = be[ 14 ];
		const b41 = be[ 3 ], b42 = be[ 7 ], b43 = be[ 11 ], b44 = be[ 15 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
		te[ 4 ] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
		te[ 8 ] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
		te[ 12 ] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
		te[ 5 ] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
		te[ 9 ] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
		te[ 13 ] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
		te[ 6 ] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
		te[ 10 ] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
		te[ 14 ] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;

		te[ 3 ] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
		te[ 7 ] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
		te[ 11 ] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
		te[ 15 ] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 4 ] *= s; te[ 8 ] *= s; te[ 12 ] *= s;
		te[ 1 ] *= s; te[ 5 ] *= s; te[ 9 ] *= s; te[ 13 ] *= s;
		te[ 2 ] *= s; te[ 6 ] *= s; te[ 10 ] *= s; te[ 14 ] *= s;
		te[ 3 ] *= s; te[ 7 ] *= s; te[ 11 ] *= s; te[ 15 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const n11 = te[ 0 ], n12 = te[ 4 ], n13 = te[ 8 ], n14 = te[ 12 ];
		const n21 = te[ 1 ], n22 = te[ 5 ], n23 = te[ 9 ], n24 = te[ 13 ];
		const n31 = te[ 2 ], n32 = te[ 6 ], n33 = te[ 10 ], n34 = te[ 14 ];
		const n41 = te[ 3 ], n42 = te[ 7 ], n43 = te[ 11 ], n44 = te[ 15 ];

		//TODO: make this more efficient
		//( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

		return (
			n41 * (
				+ n14 * n23 * n32
				 - n13 * n24 * n32
				 - n14 * n22 * n33
				 + n12 * n24 * n33
				 + n13 * n22 * n34
				 - n12 * n23 * n34
			) +
			n42 * (
				+ n11 * n23 * n34
				 - n11 * n24 * n33
				 + n14 * n21 * n33
				 - n13 * n21 * n34
				 + n13 * n24 * n31
				 - n14 * n23 * n31
			) +
			n43 * (
				+ n11 * n24 * n32
				 - n11 * n22 * n34
				 - n14 * n21 * n32
				 + n12 * n21 * n34
				 + n14 * n22 * n31
				 - n12 * n24 * n31
			) +
			n44 * (
				- n13 * n22 * n31
				 - n11 * n23 * n32
				 + n11 * n22 * n33
				 + n13 * n21 * n32
				 - n12 * n21 * n33
				 + n12 * n23 * n31
			)

		);

	}

	transpose() {

		const te = this.elements;
		let tmp;

		tmp = te[ 1 ]; te[ 1 ] = te[ 4 ]; te[ 4 ] = tmp;
		tmp = te[ 2 ]; te[ 2 ] = te[ 8 ]; te[ 8 ] = tmp;
		tmp = te[ 6 ]; te[ 6 ] = te[ 9 ]; te[ 9 ] = tmp;

		tmp = te[ 3 ]; te[ 3 ] = te[ 12 ]; te[ 12 ] = tmp;
		tmp = te[ 7 ]; te[ 7 ] = te[ 13 ]; te[ 13 ] = tmp;
		tmp = te[ 11 ]; te[ 11 ] = te[ 14 ]; te[ 14 ] = tmp;

		return this;

	}

	setPosition( x, y, z ) {

		const te = this.elements;

		if ( x.isVector3 ) {

			te[ 12 ] = x.x;
			te[ 13 ] = x.y;
			te[ 14 ] = x.z;

		} else {

			te[ 12 ] = x;
			te[ 13 ] = y;
			te[ 14 ] = z;

		}

		return this;

	}

	invert() {

		// based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ], n41 = te[ 3 ],
			n12 = te[ 4 ], n22 = te[ 5 ], n32 = te[ 6 ], n42 = te[ 7 ],
			n13 = te[ 8 ], n23 = te[ 9 ], n33 = te[ 10 ], n43 = te[ 11 ],
			n14 = te[ 12 ], n24 = te[ 13 ], n34 = te[ 14 ], n44 = te[ 15 ],

			t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
			t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
			t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
			t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;

		const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44 ) * detInv;
		te[ 2 ] = ( n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44 ) * detInv;
		te[ 3 ] = ( n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43 ) * detInv;

		te[ 4 ] = t12 * detInv;
		te[ 5 ] = ( n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44 ) * detInv;
		te[ 6 ] = ( n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44 ) * detInv;
		te[ 7 ] = ( n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43 ) * detInv;

		te[ 8 ] = t13 * detInv;
		te[ 9 ] = ( n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44 ) * detInv;
		te[ 10 ] = ( n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44 ) * detInv;
		te[ 11 ] = ( n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43 ) * detInv;

		te[ 12 ] = t14 * detInv;
		te[ 13 ] = ( n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34 ) * detInv;
		te[ 14 ] = ( n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34 ) * detInv;
		te[ 15 ] = ( n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33 ) * detInv;

		return this;

	}

	scale( v ) {

		const te = this.elements;
		const x = v.x, y = v.y, z = v.z;

		te[ 0 ] *= x; te[ 4 ] *= y; te[ 8 ] *= z;
		te[ 1 ] *= x; te[ 5 ] *= y; te[ 9 ] *= z;
		te[ 2 ] *= x; te[ 6 ] *= y; te[ 10 ] *= z;
		te[ 3 ] *= x; te[ 7 ] *= y; te[ 11 ] *= z;

		return this;

	}

	getMaxScaleOnAxis() {

		const te = this.elements;

		const scaleXSq = te[ 0 ] * te[ 0 ] + te[ 1 ] * te[ 1 ] + te[ 2 ] * te[ 2 ];
		const scaleYSq = te[ 4 ] * te[ 4 ] + te[ 5 ] * te[ 5 ] + te[ 6 ] * te[ 6 ];
		const scaleZSq = te[ 8 ] * te[ 8 ] + te[ 9 ] * te[ 9 ] + te[ 10 ] * te[ 10 ];

		return Math.sqrt( Math.max( scaleXSq, scaleYSq, scaleZSq ) );

	}

	makeTranslation( x, y, z ) {

		if ( x.isVector3 ) {

			this.set(

				1, 0, 0, x.x,
				0, 1, 0, x.y,
				0, 0, 1, x.z,
				0, 0, 0, 1

			);

		} else {

			this.set(

				1, 0, 0, x,
				0, 1, 0, y,
				0, 0, 1, z,
				0, 0, 0, 1

			);

		}

		return this;

	}

	makeRotationX( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			1, 0, 0, 0,
			0, c, - s, 0,
			0, s, c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationY( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			 c, 0, s, 0,
			 0, 1, 0, 0,
			- s, 0, c, 0,
			 0, 0, 0, 1

		);

		return this;

	}

	makeRotationZ( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			c, - s, 0, 0,
			s, c, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationAxis( axis, angle ) {

		// Based on http://www.gamedev.net/reference/articles/article1199.asp

		const c = Math.cos( angle );
		const s = Math.sin( angle );
		const t = 1 - c;
		const x = axis.x, y = axis.y, z = axis.z;
		const tx = t * x, ty = t * y;

		this.set(

			tx * x + c, tx * y - s * z, tx * z + s * y, 0,
			tx * y + s * z, ty * y + c, ty * z - s * x, 0,
			tx * z - s * y, ty * z + s * x, t * z * z + c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeScale( x, y, z ) {

		this.set(

			x, 0, 0, 0,
			0, y, 0, 0,
			0, 0, z, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeShear( xy, xz, yx, yz, zx, zy ) {

		this.set(

			1, yx, zx, 0,
			xy, 1, zy, 0,
			xz, yz, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	compose( position, quaternion, scale ) {

		const te = this.elements;

		const x = quaternion._x, y = quaternion._y, z = quaternion._z, w = quaternion._w;
		const x2 = x + x,	y2 = y + y, z2 = z + z;
		const xx = x * x2, xy = x * y2, xz = x * z2;
		const yy = y * y2, yz = y * z2, zz = z * z2;
		const wx = w * x2, wy = w * y2, wz = w * z2;

		const sx = scale.x, sy = scale.y, sz = scale.z;

		te[ 0 ] = ( 1 - ( yy + zz ) ) * sx;
		te[ 1 ] = ( xy + wz ) * sx;
		te[ 2 ] = ( xz - wy ) * sx;
		te[ 3 ] = 0;

		te[ 4 ] = ( xy - wz ) * sy;
		te[ 5 ] = ( 1 - ( xx + zz ) ) * sy;
		te[ 6 ] = ( yz + wx ) * sy;
		te[ 7 ] = 0;

		te[ 8 ] = ( xz + wy ) * sz;
		te[ 9 ] = ( yz - wx ) * sz;
		te[ 10 ] = ( 1 - ( xx + yy ) ) * sz;
		te[ 11 ] = 0;

		te[ 12 ] = position.x;
		te[ 13 ] = position.y;
		te[ 14 ] = position.z;
		te[ 15 ] = 1;

		return this;

	}

	decompose( position, quaternion, scale ) {

		const te = this.elements;

		let sx = _v1.set( te[ 0 ], te[ 1 ], te[ 2 ] ).length();
		const sy = _v1.set( te[ 4 ], te[ 5 ], te[ 6 ] ).length();
		const sz = _v1.set( te[ 8 ], te[ 9 ], te[ 10 ] ).length();

		// if determine is negative, we need to invert one scale
		const det = this.determinant();
		if ( det < 0 ) sx = - sx;

		position.x = te[ 12 ];
		position.y = te[ 13 ];
		position.z = te[ 14 ];

		// scale the rotation part
		_m1.copy( this );

		const invSX = 1 / sx;
		const invSY = 1 / sy;
		const invSZ = 1 / sz;

		_m1.elements[ 0 ] *= invSX;
		_m1.elements[ 1 ] *= invSX;
		_m1.elements[ 2 ] *= invSX;

		_m1.elements[ 4 ] *= invSY;
		_m1.elements[ 5 ] *= invSY;
		_m1.elements[ 6 ] *= invSY;

		_m1.elements[ 8 ] *= invSZ;
		_m1.elements[ 9 ] *= invSZ;
		_m1.elements[ 10 ] *= invSZ;

		quaternion.setFromRotationMatrix( _m1 );

		scale.x = sx;
		scale.y = sy;
		scale.z = sz;

		return this;

	}

	makePerspective( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const x = 2 * near / ( right - left );
		const y = 2 * near / ( top - bottom );

		const a = ( right + left ) / ( right - left );
		const b = ( top + bottom ) / ( top - bottom );

		let c, d;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			c = - ( far + near ) / ( far - near );
			d = ( - 2 * far * near ) / ( far - near );

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			c = - far / ( far - near );
			d = ( - far * near ) / ( far - near );

		} else {

			throw new Error( 'THREE.Matrix4.makePerspective(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = x;	te[ 4 ] = 0;	te[ 8 ] = a; 	te[ 12 ] = 0;
		te[ 1 ] = 0;	te[ 5 ] = y;	te[ 9 ] = b; 	te[ 13 ] = 0;
		te[ 2 ] = 0;	te[ 6 ] = 0;	te[ 10 ] = c; 	te[ 14 ] = d;
		te[ 3 ] = 0;	te[ 7 ] = 0;	te[ 11 ] = - 1;	te[ 15 ] = 0;

		return this;

	}

	makeOrthographic( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const w = 1.0 / ( right - left );
		const h = 1.0 / ( top - bottom );
		const p = 1.0 / ( far - near );

		const x = ( right + left ) * w;
		const y = ( top + bottom ) * h;

		let z, zInv;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			z = ( far + near ) * p;
			zInv = - 2 * p;

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			z = near * p;
			zInv = - 1 * p;

		} else {

			throw new Error( 'THREE.Matrix4.makeOrthographic(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = 2 * w;	te[ 4 ] = 0;		te[ 8 ] = 0; 		te[ 12 ] = - x;
		te[ 1 ] = 0; 		te[ 5 ] = 2 * h;	te[ 9 ] = 0; 		te[ 13 ] = - y;
		te[ 2 ] = 0; 		te[ 6 ] = 0;		te[ 10 ] = zInv;	te[ 14 ] = - z;
		te[ 3 ] = 0; 		te[ 7 ] = 0;		te[ 11 ] = 0;		te[ 15 ] = 1;

		return this;

	}

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 16; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 16; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];
		array[ offset + 3 ] = te[ 3 ];

		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];
		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];

		array[ offset + 8 ] = te[ 8 ];
		array[ offset + 9 ] = te[ 9 ];
		array[ offset + 10 ] = te[ 10 ];
		array[ offset + 11 ] = te[ 11 ];

		array[ offset + 12 ] = te[ 12 ];
		array[ offset + 13 ] = te[ 13 ];
		array[ offset + 14 ] = te[ 14 ];
		array[ offset + 15 ] = te[ 15 ];

		return array;

	}

}

const _v1 = /*@__PURE__*/ new Vector3();
const _m1 = /*@__PURE__*/ new Matrix4();
const _zero = /*@__PURE__*/ new Vector3( 0, 0, 0 );
const _one = /*@__PURE__*/ new Vector3( 1, 1, 1 );
const _x = /*@__PURE__*/ new Vector3();
const _y = /*@__PURE__*/ new Vector3();
const _z = /*@__PURE__*/ new Vector3();

const Z_TO_Y_UP_QUAT = Object.freeze(new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2));
const Z_TO_Y_UP_MATRIX = Object.freeze(new Matrix4().makeRotationAxis(new Vector3(1, 0, 0), -Math.PI / 2));
/**
 * @param transform A transform in a ThreeJs coordinate system
 * @returns An IElement compatible pose
 */
function convertThreeToIElementTransform(transform) {
    return {
        pos: transform.position
            ? {
                x: transform.position.x,
                y: transform.position.y,
                z: transform.position.z,
            }
            : null,
        rot: transform.quaternion
            ? {
                x: transform.quaternion.x,
                y: transform.quaternion.y,
                z: transform.quaternion.z,
                w: transform.quaternion.w,
            }
            : null,
        scale: transform.scale
            ? {
                x: transform.scale.x,
                y: transform.scale.y,
                z: transform.scale.z,
            }
            : null,
        gps: null,
        isWorldRot: false,
    };
}
/**
 * Convert from left-handed quaternion to right-handed quaternion
 *
 * @param rot The quaternion referred to a left-handed coordinate system
 * @param leftHanded whether this iElement pose is to be interpreted in a left-handed CS (Default: true)
 * @returns The input quaternion referred to a right-handed coordinate system
 */
function convertIElementToThreeRotation(rot, leftHanded = true) {
    if (!rot)
        return [0, 0, 0, 1];
    const flip = leftHanded ? -1 : 1;
    return [rot.x, rot.y, flip * rot.z, flip * rot.w];
}
/**
 * Convert from left-handed position to right-handed position
 *
 * @param pos The position referred to a left-handed coordinate system
 * @param leftHanded whether this iElement pose is to be interpreted in a left-handed CS (Default: true)
 * @returns The input position referred to a right-handed coordinate system
 */
function convertIElementToThreePosition(pos, leftHanded = true) {
    if (!pos)
        return [0, 0, 0];
    const flip = leftHanded ? -1 : 1;
    return [pos.x, pos.y, flip * pos.z];
}
/**
 * Converts from left-handed IElement into right-handed ThreeJS coordinates coordinates.
 *
 * @param pose The pose to convert into the three js coordinate system
 * @param leftHanded whether this iElement pose is to be interpreted in a left-handed CS (Default: true)
 * @returns A ThreeJs compatible transform
 */
function convertIElementToThreeTransform(pose, leftHanded = true) {
    return {
        position: convertIElementToThreePosition(pose?.pos, leftHanded),
        quaternion: convertIElementToThreeRotation(pose?.rot, leftHanded),
        scale: [pose?.scale?.x ?? 1, pose?.scale?.y ?? 1, pose?.scale?.z ?? 1],
    };
}
const X_AXIS = new Vector3();
const Y_AXIS = new Vector3();
const Z_AXIS = new Vector3();
const CROSS_PROD = new Vector3();
/**
 * Returns whether the given pose is left-handed or right-handed.
 * This is determined by checking whether the cross product of the X and Y axes
 * is co-directional with the Z axis (right handed) or not (left handed). This
 * computation is valid because we assume that all scale values are always positive.
 * This constraint will be also documented in a confluence page.
 *
 * @param pose The pose to check
 * @returns true if the pose is left-handed, false if it is right-handed
 */
function isPoseLeftHanded(pose) {
    const e = pose.elements;
    X_AXIS.set(e[0], e[1], e[2]);
    Y_AXIS.set(e[4], e[5], e[6]);
    Z_AXIS.set(e[8], e[9], e[10]);
    CROSS_PROD.crossVectors(X_AXIS, Y_AXIS);
    return CROSS_PROD.dot(Z_AXIS) < 0;
}
const TEMP_VECTOR3 = new Vector3();
const TEMP_MATRIX4$1 = new Matrix4();
/**
 * Decompose a 4x4 Matrix.
 * This function extends the built-in decompose function defined in Threejs, that has
 * a huge limitation: if the matrix contains a mirroring (the scale of an odd number of axis is negative),
 * then the scale will always have the negative value set in the x component.
 * In the viewer, we expect to have the mirroring always on the z axis, due to the change of reference system
 * from the Project API.
 * This new function allows to choose on which axis the mirroring should be applied.
 *
 * @param matrix The matrix to decompose
 * @param mirroring A flag specifying which axis should contain the flipping, if present
 * @returns The position, rotation and scale components of the matrix
 */
function decomposeMatrix(matrix, mirroring) {
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    const te = matrix.elements;
    let sx = TEMP_VECTOR3.set(te[0], te[1], te[2]).length();
    let sy = TEMP_VECTOR3.set(te[4], te[5], te[6]).length();
    let sz = TEMP_VECTOR3.set(te[8], te[9], te[10]).length();
    // if determine is negative, we need to invert one scale
    const det = matrix.determinant();
    if (det < 0) {
        switch (mirroring) {
            case "x": {
                sx = -sx;
                break;
            }
            case "y": {
                sy = -sy;
                break;
            }
            case "z": {
                sz = -sz;
                break;
            }
        }
    }
    position.x = te[12];
    position.y = te[13];
    position.z = te[14];
    // scale the rotation part
    TEMP_MATRIX4$1.copy(matrix);
    const invSX = 1 / sx;
    const invSY = 1 / sy;
    const invSZ = 1 / sz;
    TEMP_MATRIX4$1.elements[0] *= invSX;
    TEMP_MATRIX4$1.elements[1] *= invSX;
    TEMP_MATRIX4$1.elements[2] *= invSX;
    TEMP_MATRIX4$1.elements[4] *= invSY;
    TEMP_MATRIX4$1.elements[5] *= invSY;
    TEMP_MATRIX4$1.elements[6] *= invSY;
    TEMP_MATRIX4$1.elements[8] *= invSZ;
    TEMP_MATRIX4$1.elements[9] *= invSZ;
    TEMP_MATRIX4$1.elements[10] *= invSZ;
    quaternion.setFromRotationMatrix(TEMP_MATRIX4$1);
    scale.x = sx;
    scale.y = sy;
    scale.z = sz;
    return {
        position,
        quaternion,
        scale,
    };
}

const LEFT_TO_RIGHT = Object.freeze(new Matrix4().makeScale(1, 1, -1));
/** Default identity transform used when a valid transform is not available */
const DEFAULT_TRANSFORM = {
    worldMatrixMixedHd: new Matrix4().toArray(),
    worldMatrix: new Matrix4().toArray(),
    position: [0, 0, 0],
    quaternion: [0, 0, 0, 1],
    scale: [1, 1, 1],
};
/**
 * @returns the world transform of an IElement.
 * @param elementsState IElementsState describing the project
 * @param id ID of the IElement
 * @param transformOverrides Transforms to overwrite the persisted values with (only during the calculation).
 *  If defined, the global transform cache will be disabled.
 */
function getElementTransformation(elementsState, id, transformOverrides) {
    if (!id) {
        return DEFAULT_TRANSFORM;
    }
    const iElement = elementsState.iElements[id];
    // iElement might be undefined in case of `id` being invalid
    if (!iElement) {
        return DEFAULT_TRANSFORM;
    }
    // If the transforms are patched, we can't use the cache as some of the values might be invalid
    const cache = transformOverrides ? {} : elementsState.worldTransformCache;
    return getIElementWorldTransform(elementsState.iElements, iElement, cache, elementsState.areaDataSets, transformOverrides);
}
/**
 * @param pose The pose to convert to a transformation matrix.
 *  This must be given in Y-up left-handed coordinate system, as defined by the Project API.
 * @returns the corresponding transformation matrix, in Y-up right-handed coordinate system.
 */
function poseToMatrix4(pose) {
    if (!pose)
        return new Matrix4();
    return new Matrix4().compose(new Vector3(pose.pos?.x ?? 0, pose.pos?.y ?? 0, pose.pos?.z ?? 0), new Quaternion(pose.rot?.x ?? 0, pose.rot?.y ?? 0, pose.rot?.z ?? 0, pose.rot?.w ?? 1), new Vector3(pose.scale?.x ?? 1, pose.scale?.y ?? 1, pose.scale?.z ?? 1));
}
/**
 * Function to convert a Matrix in our cached world transform
 *
 * @param matrix The new world transform matrix
 * @returns the matrix decomposed in multiple arrays
 */
function computeCachedWorldTransform(matrix) {
    const matrixRhd = matrix.clone();
    if (isPoseLeftHanded(matrixRhd)) {
        matrixRhd.multiply(LEFT_TO_RIGHT);
    }
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    matrixRhd.decompose(position, quaternion, scale);
    return {
        worldMatrixMixedHd: matrix.toArray(),
        worldMatrix: matrixRhd.toArray(),
        position: [position.x, position.y, position.z],
        quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
        scale: [scale.x, scale.y, scale.z],
    };
}
/**
 * Compute the actual world matrix by taking into account the world-referred parameters
 *
 * @param transform input is the element's world transformation as if there was no isWorldXXX flags;
 *  output will be the actual world transformation of the element
 * @param elementPose The original pose of the element for which we are computing the world matrix
 * @returns The actual world matrix
 */
function accountForWorldPoseFlags(transform, elementPose) {
    // Override transform parameters that are world-referred
    if (!!elementPose?.isWorldRot ||
        !!elementPose?.isWorldScale ||
        !!elementPose?.isWorldPose) {
        // Remove the left-to-right conversion applied by the root
        transform.premultiply(LEFT_TO_RIGHT);
        const { position, quaternion, scale } = decomposeMatrix(transform, "z");
        // If the rotation is world-referred, override the one in the transform
        if (!!(elementPose.isWorldRot && elementPose.rot) ||
            elementPose.isWorldPose) {
            quaternion.fromArray(convertIElementToThreeRotation(elementPose.rot, false));
        }
        // If the scale is world-referred, override the one in the transform
        if (!!(elementPose.isWorldScale && !!elementPose.scale) ||
            elementPose.isWorldPose) {
            scale.set(elementPose.scale?.x ?? 1, elementPose.scale?.y ?? 1, elementPose.scale?.z ?? 1);
        }
        if (elementPose.isWorldPose) {
            position.set(elementPose.pos?.x ?? 0, elementPose.pos?.y ?? 0, elementPose.pos?.z ?? 0);
        }
        transform.compose(position, quaternion, scale);
        // Reapply the left-to-right conversion to the root
        transform.premultiply(LEFT_TO_RIGHT);
    }
    return transform;
}
/**
 * Check if an element is inside a CaptureTree
 *
 * @param element The element to check
 * @param iElements The map with all the projects IElements
 * @returns true if the element is inside a CaptureTree
 */
function isInsideCaptureTree(element, iElements) {
    const parent = element.parentId ? iElements[element.parentId] : undefined;
    return (element.typeHint === IElementTypeHint.captureTree ||
        (!!parent && isInsideCaptureTree(parent, iElements)));
}
/**
 * Update a WorldTransformCache for a sub-tree of a project
 *
 * @param iElements The map with all the projects IElements
 * @param cache The cache to update, used to query parent elements if needed
 * @param areaDataSets The mapping between areas and their datasets in the capture tree
 * @param rootId The root of the sub-tree to recompute, tha parent matrix should be in the cache already, default to root
 */
function updateWorldTransformCacheSubTree(iElements, cache, areaDataSets, rootId) {
    // Ensure we have a valid project
    if (Object.keys(iElements).length === 0) {
        throw new Error("Can't update world transformation from an empty project");
    }
    rootId = rootId ?? Object.values(iElements)[0]?.rootId;
    assert(rootId, "Can't update world transformation without a root element");
    // Access the root element from which invalidate and update the cache
    const iElement = iElements[rootId];
    if (!iElement) {
        throw new Error(`Requested iElement with id ${rootId} is not in the project`);
    }
    // The initial transform is the parent transform if we're not starting from the root
    const initialTransform = new Matrix4();
    const parent = iElement.parentId ? iElements[iElement.parentId] : undefined;
    if (parent) {
        const parentTransform = cache[parent.id];
        // parentTransform might be undefined in case of `parent.id` being invalid
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!parentTransform) {
            throw new Error(`Requested to re-evaluate world transform for element ${rootId} but the parent is not in cache`);
        }
        initialTransform.multiply(new Matrix4().fromArray(parentTransform.worldMatrixMixedHd));
    }
    // Recursively compute the cumulative transform for each element and update the cache
    // eslint-disable-next-line func-style -- FIXME
    const recursiveUpdate = (element, parentTransform, elementInsideCaptureTree) => {
        const insideCaptureTree = elementInsideCaptureTree ||
            element.typeHint === IElementTypeHint.captureTree;
        let { pose } = element;
        // Check if the element is a data-set in an area and set its world position as the position inside the area
        // TODO: Update this logic to introduce multi-relation between datasets and areas (https://faro01.atlassian.net/browse/SWEB-4470)
        const areaData = findDataSetAreaAndPose(element.id, areaDataSets, iElements);
        if (areaData) {
            // Poses from alignment edges are always interpreted as left-handed
            parentTransform.fromArray(cache[areaData[0]].worldMatrixMixedHd);
            pose = areaData[1];
        }
        const poseMatrix = computeLocalPoseMatrix(pose, element, insideCaptureTree);
        const transform = parentTransform.multiply(poseMatrix);
        const worldTransform = computeCachedWorldTransform(accountForWorldPoseFlags(transform, pose));
        cache[element.id] = worldTransform;
        for (const id of element.childrenIds ?? []) {
            // Recurse only if the child is loaded in the slice
            const child = iElements[id];
            // iElements might be undefined in case of `id` being invalid
            if (child) {
                // As multiple children will want to compose on this node transform we need a clone for each child
                recursiveUpdate(child, transform.clone(), insideCaptureTree);
            }
        }
    };
    // Start to recurse from the passed iElement
    recursiveUpdate(iElement, initialTransform, isInsideCaptureTree(iElement, iElements));
}
/**
 * @param dataSetId id of the dataset
 * @param areaDataSets the mapping between areas and their datasets
 * @param elements of the projects
 * @returns the id of the area containing this dataset and the local pose of the dataset in the area if available
 *
 * Will ignore datasets from the bi-tree
 */
function findDataSetAreaAndPose(dataSetId, areaDataSets, elements) {
    for (const [areaId, dataSets] of Object.entries(areaDataSets)) {
        const dataSet = dataSets?.find((dataSet) => dataSet.elementId === dataSetId);
        if (!dataSet)
            continue;
        const dataSetElement = elements[dataSet.elementId];
        const parentElement = dataSetElement?.parentId
            ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- FIXME
                elements[dataSetElement?.parentId]
            : undefined;
        if (dataSetElement &&
            parentElement &&
            !(parentElement.type === IElementType.section &&
                parentElement.typeHint === IElementTypeHint.dataSession)) {
            return [
                areaId,
                {
                    gps: null,
                    isWorldRot: false,
                    ...dataSet.pose,
                },
            ];
        }
    }
}
/**
 * Compute an iElement world transform using a support cache
 *
 * If the transform is already in the cache returns it
 * If not compute the element transform using the cache for the parents if needed
 *
 * @param iElements All the available iElements in the project
 * @param iElement The IElement we want the world cached transform
 * @param cache The support cache
 * @param areaDataSets The mapping between areas and their datasets in the capture tree
 * @param transformOverrides Transforms to overwrite the persisted values with during the calculation
 * @returns The computed element world cached transform
 */
function getIElementWorldTransform(iElements, iElement, cache, areaDataSets, transformOverrides) {
    // Return directly if overridden or cached
    const worldTransform = transformOverrides?.global[iElement.id] ?? cache[iElement.id];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- FIXME
    if (worldTransform) {
        return worldTransform;
    }
    // Build the new transform
    const transform = new Matrix4();
    // Get parent matrix, will compute recursively all the parents are needed
    if (iElement.parentId) {
        const parent = iElements[iElement.parentId];
        if (parent) {
            transform.fromArray(getIElementWorldTransform(iElements, parent, cache, areaDataSets, transformOverrides).worldMatrixMixedHd);
        }
    }
    // Compose local transform with the parents
    let localPose = transformOverrides?.local[iElement.id];
    const insideCaptureTree = isInsideCaptureTree(iElement, iElements);
    if (!localPose) {
        localPose = iElement.pose;
        // Check if the element is a data-set in an area and set its world position as the position inside the area
        // TODO: Update this logic to introduce multi-relation between datasets and areas (https://faro01.atlassian.net/browse/SWEB-4470)
        const areaData = findDataSetAreaAndPose(iElement.id, areaDataSets, iElements);
        if (areaData) {
            const areaId = areaData[0];
            let edge = cache[areaId];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- FIXME
            if (!edge) {
                const area = iElements[areaId];
                assert(area, "Area IElement not downloaded yet");
                edge = getIElementWorldTransform(iElements, area, cache, areaDataSets, transformOverrides);
            }
            // Poses from alignment edges are always interpreted as left-handed
            transform.fromArray(edge.worldMatrixMixedHd);
            localPose = areaData[1];
        }
    }
    const poseMatrix = computeLocalPoseMatrix(localPose, iElement, insideCaptureTree);
    transform.multiply(poseMatrix);
    // Consider isWorldXXX flags in the computed transform
    return computeCachedWorldTransform(accountForWorldPoseFlags(transform, localPose));
}
/**
 *
 * @param pose Input pose as read from the Project API.
 * @param iElement IElement whose pose must be computed
 * @param insideCaptureTree Whether the iElement is inside the capture tree or not
 * @returns The iElement's local pose, expressed as a 4x4 matrix in the Viewer's CS
 */
function computeLocalPoseMatrix(pose, iElement, insideCaptureTree) {
    const refCoordSystemTransform = computeReferenceSystemTransform(iElement, insideCaptureTree);
    const poseMatrix = poseToMatrix4(pose);
    poseMatrix.premultiply(refCoordSystemTransform.leftMatrix);
    poseMatrix.multiply(refCoordSystemTransform.rightMatrix);
    return poseMatrix;
}
/**
 * @returns The reference coordinate system matrices for a specific iElement
 * @param iElement The input IElement.
 * @param insideCaptureTree Whether the element is inside the capture tree or not.
 */
function computeReferenceSystemTransform(iElement, insideCaptureTree) {
    return convertReferenceSystemProperties(computeReferenceSystemProperties(iElement, insideCaptureTree));
}
/**
 * @returns The matrices obtained by combining the properties of an IRefCoordSystemTransform
 * @param refCoordSystemTransform The input IRefCoordSystemTransform
 */
function convertReferenceSystemProperties(refCoordSystemTransform) {
    const pos = new Vector3(refCoordSystemTransform?.pos?.x ?? 0, refCoordSystemTransform?.pos?.y ?? 0, refCoordSystemTransform?.pos?.z ?? 0);
    const rot = new Quaternion(refCoordSystemTransform?.rot?.x ?? 0, refCoordSystemTransform?.rot?.y ?? 0, refCoordSystemTransform?.rot?.z ?? 0, refCoordSystemTransform?.rot?.w ?? 1);
    const scale = new Vector3(refCoordSystemTransform?.scale?.x ?? 1, refCoordSystemTransform?.scale?.y ?? 1, refCoordSystemTransform?.scale?.z ?? 1);
    const leftMatrix = new Matrix4().makeScale(1, 1, refCoordSystemTransform?.preScaleZ ?? 1);
    return {
        rightMatrix: new Matrix4().compose(pos, rot, scale),
        leftMatrix,
    };
}
/**
 * @returns The reference coordinate system properties for a specific iElement
 * @param iElement The input IElement.
 * @param insideCaptureTree Whether the element is inside the capture tree or not.
 */
function computeReferenceSystemProperties(iElement, insideCaptureTree) {
    if (iElement.refCoordSystemMatrix) {
        return iElement.refCoordSystemMatrix;
    }
    // Returning a specific reference system matrix according to the iElement type
    switch (iElement.type) {
        case IElementType.section: {
            // Since the PCSG adds Focus Scan with a right handed pose, but all its children and siblings are left handed
            // we add the flipping to the focus dataset, so that the sub-tree can be entirely expressed with
            // a right handed system. To compensate this flip, we also need to make the structuredE57 right handed
            // so we pre-flip and post-flip its pose
            // (see https://faroinc.sharepoint.com/:p:/r/sites/SWEB/Resources/Documents/SWEB-4941-capture-tree-handness.pptx?d=w4260bf7d332a4c22bfe422b97d7fc081&csf=1&web=1&e=jmm0P6&nav=eyJzSWQiOjI1NywiY0lkIjoyNDM3MDIyMDYxfQ)
            if (insideCaptureTree) {
                if (iElement.typeHint === IElementTypeHint.dataSetFocus ||
                    iElement.typeHint === IElementTypeHint.dataSetEls ||
                    iElement.typeHint === IElementTypeHint.dataSetCapture ||
                    iElement.typeHint === IElementTypeHint.dataSetOrbis ||
                    iElement.typeHint === IElementTypeHint.dataSetSiteScape) {
                    return {
                        pos: null,
                        rot: null,
                        scale: { x: 1, y: 1, z: -1 },
                        preScaleZ: null,
                    };
                }
                if (iElement.typeHint === IElementTypeHint.structuredE57) {
                    return {
                        pos: null,
                        rot: null,
                        scale: { x: 1, y: 1, z: -1 },
                        preScaleZ: -1,
                    };
                }
            }
            break;
        }
        case IElementType.projectRoot:
            return { pos: null, rot: null, scale: null, preScaleZ: -1 };
        case IElementType.img360: {
            // Hack needed because we want to represent panos as Z-Up spheres with the center going toward -X,
            // but the current pano in the data models by default expect a geometry that is Y-up with the center going toward +X
            return {
                pos: null,
                rot: { x: 0, y: -Math.SQRT1_2, z: Math.SQRT1_2, w: 0 },
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: insideCaptureTree ? -1 : null,
            };
        }
        // Img sheets are represented by quads in the XY plane. To view them in our
        // Y-up ref system, we rotate them 90 degrees around the X axis.
        case IElementType.imgSheet: {
            return {
                pos: null,
                rot: {
                    x: Math.SQRT1_2,
                    y: 0,
                    z: 0,
                    w: Math.SQRT1_2,
                },
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: insideCaptureTree ? -1 : null,
            };
        }
        case IElementType.imgSheetTiled: {
            // Tiled image sheet in the ProjectAPI have the top left corner at the origin
            // We apply an offset to in order to move at the origin the bottom left corner,
            // like simple image sheets.
            // The geometry is based on a 100x100 square, hence why we offset by 100
            const Y_OFFSET = -100;
            return {
                pos: { x: 0, y: 0, z: Y_OFFSET },
                rot: {
                    x: Math.SQRT1_2,
                    y: 0,
                    z: 0,
                    w: Math.SQRT1_2,
                },
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: insideCaptureTree ? -1 : null,
            };
        }
        case IElementType.model3d: {
            if (iElement.typeHint === IElementTypeHint.node) {
                // If we enter here, we are handling a markup annotation. Markup annotations
                // should be rotate 180 degrees around their local vertical, otherwise they
                // appear flipped left-to-right.
                return {
                    pos: null,
                    rot: { x: 0, y: 1, z: 0, w: 0 },
                    scale: { x: 1, y: 1, z: -1 },
                    preScaleZ: insideCaptureTree ? -1 : null,
                };
            }
            return {
                pos: null,
                rot: null,
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: null,
            };
        }
        case IElementType.pointCloudCpe:
        case IElementType.pointCloudE57:
        case IElementType.pointCloudGeoSlam:
        case IElementType.pointCloudLaz:
        case IElementType.pointCloudStream:
        case IElementType.pointCloudElsRaw:
            return {
                pos: null,
                rot: null,
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: insideCaptureTree ? -1 : null,
            };
        case IElementType.model3dStream:
            return {
                pos: null,
                rot: null,
                scale: { x: 1, y: 1, z: -1 },
                preScaleZ: null,
            };
        case IElementType.group: {
            if (insideCaptureTree && iElement.typeHint === IElementTypeHint.nodes) {
                return {
                    pos: null,
                    rot: null,
                    scale: { x: 1, y: 1, z: -1 },
                    preScaleZ: -1,
                };
            }
            break;
        }
        case IElementType.measurePolygon:
        case IElementType.markupPolygon: {
            if (insideCaptureTree) {
                return {
                    pos: null,
                    rot: null,
                    scale: null,
                    preScaleZ: -1,
                };
            }
            break;
        }
    }
    return null;
}
const TEMP_MATRIX4 = new Matrix4();
/**
 * Given a right-handed local pose computed in the Viewer reference system,
 * extracts all the properties that should be set in the pose of the iElement
 * when updating the pose in the ProjectAPI, by extracting the contributions of
 * the refCoordSystemMatrix
 *
 * @param element The element whose local pose should be computed
 * @param localPose The local pose of the element, containing the refCoordSystemMatrix contribution
 * @param isInsideCaptureTree A flag specifying if the iElement is in the CaptureTree subtree
 * @param elementWorldTransform The cached world transform of the element
 * @param parentWorldTransform The chaced world transform of the parent element
 * @returns The pose properties of the iElement
 */
function extractProjectApiLocalPose(element, localPose, isInsideCaptureTree, elementWorldTransform, parentWorldTransform) {
    const refCoordSystemTransfromProperties = computeReferenceSystemProperties(element, isInsideCaptureTree);
    const { leftMatrix, rightMatrix } = convertReferenceSystemProperties(refCoordSystemTransfromProperties);
    const matrix = localPose.clone();
    // The world matrix of the two iElements (parent and element) could be left handed. If that's the case, it means that
    // when the cache computed the right-handed world matrix, it applied a post-mirroring to the matrix.
    // We need to compensate that when extracting the pose to send to the Project API
    if (isPoseLeftHanded(TEMP_MATRIX4.fromArray(parentWorldTransform.worldMatrixMixedHd))) {
        matrix.premultiply(LEFT_TO_RIGHT);
    }
    if (isPoseLeftHanded(TEMP_MATRIX4.fromArray(elementWorldTransform.worldMatrixMixedHd))) {
        matrix.multiply(LEFT_TO_RIGHT);
    }
    matrix
        .premultiply(leftMatrix)
        .multiply(rightMatrix.invert());
    const pos = new Vector3();
    const rot = new Quaternion();
    const scale = new Vector3();
    matrix.decompose(pos, rot, scale);
    assert(scale.x > 0 && scale.y > 0 && scale.z > 0, "Invalid iElement local pose");
    return {
        pos: { x: pos.x, y: pos.y, z: pos.z },
        rot: { x: rot.x, y: rot.y, z: rot.z, w: rot.w },
        scale: { x: scale.x, y: scale.y, z: scale.z },
        refCoordSystemMatrix: refCoordSystemTransfromProperties,
    };
}

/**
 * @param state The state of the store
 * @returns All the iElements in the project loaded by the viewer
 */
function selectAllIElements(state) {
    return state.iElements.iElements;
}
/**
 * @param state The state of the store
 * @returns All the area datasets in the project loaded by the viewer
 */
function selectAllAreaDatasets(state) {
    return state.iElements.areaDataSets;
}
/**
 * @returns all loaded IElements.
 * @param state The current application state
 */
const selectLoadedIElements = createSelector([selectAllIElements], (iElements) => Object.values(iElements).filter(isValid));
/**
 * @returns The IElement with the given ID, or `undefined` if the IElement is not loaded yet or the ID is not defined.
 * @param id ID of IElement
 */
function selectIElement(id) {
    return ({ iElements }) => id ? iElements.iElements[id] : undefined;
}
/**
 * @returns A list of iElements given their ids or undefined if the id does not
 * belong to any known iElement
 * @param state The state of the store
 * @param ids The list of ids whose iElement should be collected
 */
const selectIElements = createSelector([
    (state) => state.iElements.iElements,
    (state, ids) => ids,
], (iElements, ids) => ids.map((id) => iElements[id]));
/**
 * @param state AppState
 * @returns the id of the current project
 */
function selectProjectId(state) {
    return state.iElements.projectId;
}
/**
 * @param state AppState
 * @returns the rootId of the current project
 */
function selectRootId(state) {
    return state.iElements.rootId;
}
/**
 * @param state AppState
 * @returns the name of the current project
 */
function selectProjectName(state) {
    return state.iElements.projectName;
}
/**
 * @param state AppState
 * @returns the status of the current project
 */
function selectProjectAccessLevel(state) {
    return state.iElements.projectAccessLevel;
}
/**
 * @param state AppState
 * @returns the url for the dashboard of this project
 */
function selectDashboardUrl(state) {
    return state.iElements.dashboardUrl;
}
/**
 * @returns the company ID for the current loaded project
 * @param state AppState
 */
function selectCompanyId(state) {
    return state.iElements.companyId;
}
/**
 * @returns Recursively gets all parents of the given element.
 *          Ordered ascending by distance to the passed IElement.
 * @param id ID of leave IElement
 */
function selectIElementAncestors(id) {
    return ({ iElements }) => {
        const ancestors = [];
        if (!id) {
            return ancestors;
        }
        let iElement = iElements.iElements[id];
        // iElement might be undefined in case of `id` being invalid
        if (!iElement) {
            return [];
        }
        while (iElement?.parentId) {
            ancestors.push(iElement.parentId);
            iElement = iElements.iElements[iElement.parentId];
        }
        return ancestors;
    };
}
/**
 * @returns true iff ancestorId is an ancestor of childId
 * @param ancestorId ID of ancestor
 * @param childId ID of child
 */
function selectIsIElementAncestorOf(ancestorId, childId) {
    return (state) => {
        let iElement = selectIElement(childId)(state);
        if (!iElement)
            return false;
        while (iElement.parentId) {
            if (iElement.parentId === ancestorId)
                return true;
            iElement = selectIElement(iElement.parentId)(state);
            if (!iElement)
                return false;
        }
        return false;
    };
}
/**
 * @returns All children of an IElement
 * @param id ID of parent IElement
 */
function selectIElementChildren(id) {
    return ({ iElements }) => {
        const element = iElements.iElements[id];
        return element?.childrenIds
            ? element.childrenIds
                .map((childId) => iElements.iElements[childId])
                .filter(isValid)
            : [];
    };
}
/**
 * @returns true if the given element is a section of location marker
 * @param id id of element
 */
function selectIsLocationMarkerSection(id) {
    return (state) => {
        const iElement = selectIElement(id)(state);
        if (!iElement)
            return false;
        if (iElement.type !== IElementType.section)
            return false;
        if (iElement.childrenIds?.length !== 1)
            return false;
        const timeseries = selectIElement(iElement.childrenIds[0])(state);
        if (!timeseries)
            return false;
        return (timeseries.type === IElementType.timeSeries &&
            (!timeseries.childrenIds || timeseries.childrenIds.length === 0));
    };
}
/**
 * @returns Root IElement of the project
 * @throws if the rootId does not point to a IElementProjectRoot
 */
function selectRootIElement({ iElements, }) {
    const root = iElements.rootId
        ? iElements.iElements[iElements.rootId]
        : undefined;
    if (!root)
        return;
    assert(isIElementProjectRoot(root));
    return root;
}
/**
 * @returns the world transform of an IElement.
 * @param id ID of the IElement
 * @param transformOverrides Transforms to overwrite the persisted values with (only during the calculation).
 *  If defined, the global transform cache will be disabled.
 */
function selectIElementWorldTransform(id, transformOverrides) {
    return (state) => getElementTransformation(state.iElements, id, transformOverrides);
}
/**
 * Returns the world pose matrix of a given iElement. The returned pose matrix
 * is guaranteed to be right-handed, and is therefore interoperable with three.js
 * and with the Viewer's computations.
 *
 * @returns The world transform of an IElement, as a Matrix4Tuple.
 * @param id ID of the IElement
 * @param transformOverrides Transforms to overwrite the persisted values with (only during the calculation).
 *  If defined, the global transform cache will be disabled.
 */
function selectIElementWorldMatrix(id, transformOverrides) {
    return (state) => selectIElementWorldTransform(id, transformOverrides)(state).worldMatrix;
}
/**
 * @returns the world transform of a list of IElements.
 * @param ids IDs of the IElements
 */
function selectIElementsWorldTransform(ids) {
    return (state) => ids.map((id) => selectIElementWorldTransform(id)(state));
}
/**
 * @returns the world position of an IElement.
 *          Zero vector if the ID is undefined.
 * @param id ID of the IElement
 */
function selectIElementWorldPosition(id) {
    return (state) => selectIElementWorldTransform(id)(state).position;
}
/**
 * @returns the world position of a list of IElements.
 * @param ids IDs of the IElements
 */
function selectIElementsWorldPosition(ids) {
    return (state) => ids.map((id) => selectIElementWorldPosition(id)(state));
}
/**
 * @returns distance between the given IElement and the active floorplan (in meters)
 * @param id ID of the IElement
 * @param activeImgSheetId ID of the current active imageSheet
 */
function selectAltitudeDifferenceToFloor(id, activeImgSheetId) {
    return (state) => {
        if (!activeImgSheetId) {
            return 0;
        }
        const worldPos = selectIElementWorldPosition(id)(state);
        const imgSheetWorldPos = selectIElementWorldPosition(activeImgSheetId)(state);
        return worldPos[1] - imgSheetWorldPos[1];
    };
}
/**
 * @returns the parent Timeseries of an  iElement or the direct timeseries child of a section
 * @param id ID of the IElement
 */
function selectIElementTimeseries(id) {
    return (state) => {
        const iElement = selectIElement(id)(state);
        let timeSeries = selectChildDepthFirst(iElement, isIElementTimeseries, 1)(state);
        if (timeSeries)
            return timeSeries;
        timeSeries = selectAncestor(iElement, isIElementTimeseries)(state);
        if (!timeSeries) {
            throw Error(`IElement with id ${id} is not part of a timeseries`);
        }
        return timeSeries;
    };
}
/**
 * @returns the Panorama iElement for the passed iElement,
 *      iff the iElement is or is inside the section of a single panorama.
 * @param id ID of the IElement
 */
function selectIElementPanoramaElement(id) {
    return (state) => {
        if (!id) {
            return;
        }
        const iElement = selectIElement(id)(state);
        if (!iElement)
            return;
        const children = selectIElementChildren(id)(state);
        const panoramaChild = children.find((child) => isIElementPanoramaImage(child));
        if (panoramaChild) {
            return panoramaChild;
        }
        else if (iElement.parentId) {
            return selectIElementPanoramaElement(iElement.parentId)(state);
        }
    };
}
/**
 * @returns the elements in the project of the selected types typed as IElement[] or narrowed if a type guard was used
 * @param guard the list of types we want or a type guard
 * @param ancestorId Optional, Root element to begin the search to only include a sub tree
 */
function selectAllIElementsOfType(guard, ancestorId) {
    return (state) => {
        const loadedIElements = selectLoadedIElements(state);
        // Keep implementation compatible with previous API
        const elements = Array.isArray(guard)
            ? loadedIElements.filter((el) => validateKnownIElementTypes(el.type) && guard.includes(el.type))
            : loadedIElements.filter(guard);
        // Filter for ancestor after type filtering as in this way is less expensive as less
        // nodes are tested
        // We should rethink this for huge projects where a tree traversal approach from the ancestor may be faster
        if (ancestorId) {
            return elements.filter((el) => selectIsIElementAncestorOf(ancestorId, el.id)(state));
        }
        return elements;
    };
}
/**
 * @returns From each time series in the project, selects the panorama image that is closest in time to
 * the time of a reference element, excluding panos represented in odometry paths.
 * @param referenceId the id of the element to use as a reference representing target time period
 * @param startFromId the id of an element to use as the root element of the search
 */
function selectClosestPanosInTime(referenceId, startFromId) {
    return (state) => {
        const { rootId } = state.iElements;
        const reference = selectIElement(referenceId)(state);
        if (!rootId || !reference) {
            return [];
        }
        // If we're inside a data session search the data session panos first
        if (isIElementSectionDataSession(reference)) {
            const dataSessionPanos = selectChildrenDepthFirst(reference, isIElementImg360)(state);
            if (dataSessionPanos.length > 0) {
                return dataSessionPanos;
            }
        }
        const root = startFromId
            ? selectIElement(startFromId)(state)
            : state.iElements.iElements[rootId];
        const panos = [];
        // Search in rooms timeseries
        const rooms = selectChildrenDepthFirst(root, (el) => isIElementTimeseries(el, IElementTypeHint.room))(state);
        for (const series of rooms) {
            const timeChildren = selectIElementChildren(series.id)(state).map((e) => e.id);
            const closest = selectClosestIElementInTime(referenceId, timeChildren)(state);
            if (closest) {
                const pano = selectIElementPanoramaElement(closest.id)(state);
                if (pano && isIElementImg360(pano)) {
                    panos.push(pano);
                }
            }
        }
        // Search in all the other data sessions
        const dataSessions = selectChildrenDepthFirst(root, isIElementSectionDataSession)(state);
        const referenceDataSession = pickClosestInTime(reference, dataSessions);
        if (referenceDataSession) {
            const bestSessions = dataSessions.filter((s) => areCreatedOnSameDate(s, referenceDataSession));
            // If we got one and has 360 add them to the list
            for (const dataSession of bestSessions) {
                panos.push(...selectChildrenDepthFirst(dataSession, (el) => isIElementImg360(el) && !isIElementPanoInOdometryPath(el))(state));
            }
        }
        return panos;
    };
}
/**
 * @returns the iElement closest in time to a reference element from a provided list, excluding panos represented in odometry paths.
 * @param referenceId ID of the reference IElement
 * @param candidateElementIds IDs of the potential IElements to chose from
 *
 * Element cloned from a videomode projects are ignored as they're duplicates of nodes
 * that already exists in the original VideoRecording path
 */
function selectClosestIElementInTime(referenceId, candidateElementIds) {
    return (state) => {
        if (!referenceId || !candidateElementIds?.length) {
            return;
        }
        // Ignore element cloned from videomode projects
        const candidates = candidateElementIds
            .map((id) => selectIElement(id)(state))
            .filter((el) => !!el && !isIElementVideoModeCopy(el));
        const iElement = selectIElement(referenceId)(state);
        if (!iElement)
            return;
        const referenceTime = Date.parse(iElement.createdAt);
        let closest = candidates[0];
        if (!closest)
            return;
        let closestTimeDiff = Math.abs(Date.parse(closest.createdAt) - referenceTime);
        for (const element of candidates) {
            if (!element)
                continue;
            const timeDiff = Math.abs(Date.parse(element.createdAt) - referenceTime);
            if (timeDiff < closestTimeDiff) {
                closest = element;
                closestTimeDiff = timeDiff;
            }
        }
        return closest;
    };
}
/**
 * @returns the type of an iElement.
 * @param id ID of the IElement
 */
function selectIElementType(id) {
    return (state) => {
        if (!id)
            return undefined;
        const iElement = selectIElement(id)(state);
        return iElement?.type;
    };
}
/**
 * @returns the Panorama section of the passed iElement,
 *      iff the iElement is one or is inside of one.
 *  A Panorama section is a section that contains a Panorama Image as a direct child.
 * @param id ID of the IElement
 */
function selectIElementPanoramaSection(id) {
    return (state) => {
        const iElement = selectIElement(id)(state);
        if (!iElement)
            return;
        const children = selectIElementChildren(id)(state);
        const panoramaChild = children.find((child) => isIElementPanoramaImage(child));
        if (panoramaChild) {
            return iElement;
        }
        else if (iElement.parentId) {
            return selectIElementPanoramaSection(iElement.parentId)(state);
        }
    };
}
/**
 * @param timeSeriesId The id of the timeseries to query
 * @param targetId Id of the element we want the section of
 * @returns the section in the timeseries containing the IElement with the passed targetId
 */
function selectTimeseriesSection(timeSeriesId, targetId) {
    return (state) => {
        const target = selectIElement(targetId)(state);
        return selectAncestor(target, (el) => el.parentId === timeSeriesId)(state);
    };
}
/**
 * @returns Total number of aligned Point Clouds that are inside the provided floor,
 *   with the option to only count aligned PCs
 * @param floor The floor to check
 * @param onlyAlignment Whether or not to check only for aligned Point Clouds
 */
function selectNumberOfPointCloudsOnFloor(floor, onlyAlignment = false) {
    return (state) => selectChildrenDepthFirst(floor, (pointCloud) => isIElementGenericPointCloudStream(pointCloud) &&
        (!onlyAlignment || selectIsPointCloudAligned(pointCloud)(state)))(state).length;
}
/**
 * @param element The pointcloud or cad element to check if it's aligned
 * @returns Whether a given element is aligned
 */
function selectIsElementAligned(element) {
    return (state) => {
        if (!element)
            return false;
        if (isIElementSectionDataSession(element)) {
            return selectIsPointCloudAligned(element)(state);
        }
        return false;
    };
}
/**
 * @param pointCloud The pointcloud IElementSection that is an IElementSectionLaserScans
 * or a child to check
 * @returns Whether a given pointcloud is aligned
 */
function selectIsPointCloudAligned(pointCloud) {
    return (state) => {
        if (!pointCloud) {
            return false;
        }
        // Making sure we are always working on the section data session.
        const dataSession = selectAncestor(pointCloud, isIElementSectionDataSession)(state);
        if (!dataSession) {
            return false;
        }
        return isValidPose(dataSession.pose);
    };
}
/**
 * @returns whether the current data session should be considered aligned
 * @param dataSession the data session or data session to check
 */
function selectIsDataSessionAligned(dataSession) {
    return (state) => 
    // Until they can be aligned in the staging area and have proper metadata to indicate their alignment state,
    // consider geoslam data sets as unaligned to each other.
    !selectChildDepthFirst(dataSession, isIElementSectionGeoslam)(state);
}
/**
 * @returns true if the floor has no scale set and at least a video recording is present in it
 * @param floor the floor element to check the scale of
 * @param requireVideoData require the project to contain video data to mark an area un-scaled
 */
function selectIsFloorWithoutScale(floor, requireVideoData = true) {
    return (state) => !!floor &&
        isIElementAreaSection(floor) &&
        !floor.pose?.scale &&
        !floor.pose?.isWorldScale &&
        (!requireVideoData ||
            !!selectChildDepthFirst(floor, (el) => isIElementDataSetVideoWalk(el) || isIElementVideoRecording(el))(state));
}
/**
 * @returns whether a list of elements all have an accurate scale relative to the real world.
 * Either directly or implicitly through one of theirs ancestors.
 * This is useful to determine e.g. if measurements on these element are meaningful.
 * @param elements the elements to check
 */
function selectHaveElementsAccurateWorldScale(elements) {
    return (state) => {
        if (!elements.length)
            return false;
        return !elements.some((element) => !selectAncestor(element, (ancestor) => !!ancestor.pose?.isWorldScale ||
            // Assume if an area has a scale, that it is an accurate world scale
            (isIElementAreaSection(ancestor) && !!ancestor.pose?.scale) ||
            // old orbis data sets don't have the world scale flag set as they should have
            isIElementSectionGeoslam(ancestor) ||
            // Assume that datasets within the capture tree have a defined world scale through the alignment process
            isCaptureTreeRoot(ancestor))(state));
    };
}
/**
 * Selector returns the *existing* IElement where a new Pointcloud will be attached to.
 * Returns a Pointcloud Timeseries if it already exists or the AreaSection otherwise.
 *
 * @param sheet The sheet to find the pointcloud root for
 * @returns IElement or undefined if the project is malformed
 */
function selectRootForNewPointCloud(sheet) {
    return (state) => {
        const areaSection = selectAncestor(sheet, (ancestor) => isIElementSection(ancestor) &&
            ancestor.typeHint === IElementTypeHint.area)(state);
        const laserScanTimeseries = selectChildDepthFirst(areaSection, isIElementTimeseriesDataSession)(state);
        return laserScanTimeseries ?? areaSection;
    };
}
/**
 * Selector returns the *existing* IElement where a new CAD will be attached to.
 * Returns a CAD Group if it already exists or the AreaSection otherwise.
 *
 * @param sheet The sheet to find the pointcloud root for
 * @returns IElement or undefined if the project is malformed
 */
function selectRootForNewCAD(sheet) {
    return (state) => {
        const areaSection = selectAncestor(sheet, (ancestor) => isIElementSection(ancestor) &&
            ancestor.typeHint === IElementTypeHint.area)(state);
        const bimModelGroup = selectChildDepthFirst(areaSection, isIElementBimModelGroup)(state);
        return bimModelGroup ?? areaSection;
    };
}
/**
 * @returns all IElements that are above the first currently selected element,
 *    that can be considered active as well
 * @param selectedIds The ids of the selected iElements
 */
function selectActiveTree(selectedIds) {
    return (state) => {
        const root = selectRootIElement(state);
        const ancestors = selectIElementAncestors(selectedIds[0])(state);
        const activeElements = ancestors.concat(selectedIds);
        /**
         * @returns all rendered children.
         * @param iElement Parent IElement
         */
        function getRenderedChildren(iElement) {
            const children = selectIElementChildren(iElement.id)(state);
            if (children.length === 0) {
                return [];
            }
            // Handle xOr groups and time series
            if ((isIElementGroup(iElement) && iElement.xOr) ||
                iElement.type === "TimeSeries") {
                // Only show the child that is an ancestor of the currently selected element
                const selectedChild = children.find((child) => activeElements.includes(child.id));
                // Fall back to the first child
                return [selectedChild ?? children[0]];
            }
            return children;
        }
        /**
         * @returns the rendered iElements with the given root, recursively
         * @param root IElement to start from
         */
        function getRenderedIElements(root) {
            if (!root) {
                return {};
            }
            let renderedElements = { [root.id]: root };
            for (const child of getRenderedChildren(root)) {
                const renderedChildren = getRenderedIElements(child);
                renderedElements = {
                    ...renderedElements,
                    ...renderedChildren,
                };
            }
            return renderedElements;
        }
        return getRenderedIElements(root);
    };
}
/**
 * @returns true, if all the selected ids are of panorama elements, else returns false
 * @param selectedIds The ids of the selected iElements
 */
function selectAreAllSelectedIdsPanoramas(selectedIds) {
    return (state) => !selectedIds.some((id) => selectIElementPanoramaSection(id)(state) === undefined);
}
/**
 * @returns a list of all the 3D models that are not related with any panorama image
 * @param state Current state
 */
function selectNonPano3dModels(state) {
    return selectLoadedIElements(state)
        .filter(isIElementModel3d)
        .filter((iElement) => {
        if (selectIElementPanoramaSection(iElement.id)(state) !== undefined) {
            return false;
        }
        const fileExtension = getFileExtension(iElement.uri);
        return fileExtension === "gltf" || fileExtension === "glb";
    });
}
/**
 * @returns the recent child for a given iElements based on their created date
 * @param id Id of the IElement
 */
function selectRecentChild(id) {
    return (state) => {
        const children = selectIElementChildren(id)(state);
        children.sort((childA, childB) => compareDateTimes(childA.createdAt, childB.createdAt));
        return children.pop();
    };
}
/**
 * @returns if an iElement is a child of rooms or not
 * @param id - ID of an iElement
 */
function selectIsChildOfRooms(id) {
    return (state) => {
        const el = selectIElement(id)(state);
        const parentRoom = selectAncestor(el, (parent) => parent.type === "Group" && parent.typeHint === "Rooms")(state);
        return parentRoom !== undefined;
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc
function selectAncestor(element, predicate) {
    return (state) => {
        if (!element)
            return;
        if (predicate(element))
            return element;
        while (element?.parentId) {
            element = selectIElement(element.parentId)(state);
            if (element && predicate(element))
                return element;
        }
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc
function selectChildDepthFirst(element, predicate, maxDepth) {
    return (state) => selectChildrenDepthFirst(element, predicate, maxDepth, (children) => children.length === 1)(state)[0];
}
// eslint-disable-next-line jsdoc/require-jsdoc
function selectChildrenDepthFirst(element, predicate, maxDepth, shouldStop = () => false) {
    return (state) => {
        const found = [];
        if (!element)
            return found;
        // A stack of elements to check with their depth from the starting element
        const stack = [{ element, depth: 0 }];
        while (stack.length > 0) {
            // Pop the element and depth from the stack
            const toCheck = stack.pop();
            if (!toCheck)
                continue;
            const { element, depth } = toCheck;
            // Check the search and early stop conditions
            if (predicate(element)) {
                found.push(element);
                if (shouldStop(found)) {
                    return found;
                }
            }
            // Don't compute children if the depth is already at the max allowed depth
            if (!element.childrenIds ||
                (maxDepth !== undefined && depth === maxDepth)) {
                continue;
            }
            // Check for children in reverse order to maintain order when we pop from the stack
            for (let idx = element.childrenIds.length - 1; idx >= 0; --idx) {
                const child = selectIElement(element.childrenIds[idx])(state);
                if (child) {
                    stack.push({ element: child, depth: depth + 1 });
                }
            }
        }
        return found;
    };
}
/**
 *
 * @param img The argument pano image
 * @returns Whether the pano image has a valid pose
 */
function selectHasValidPose(img) {
    return (state) => {
        if (!img) {
            return false;
        }
        /**
         *
         * @param pose The argument pose
         * @returns Whether the argument pose is a null pose
         */
        function isNullPose(pose) {
            return !pose || (!pose.pos && !pose.rot && !pose.scale);
        }
        const section = selectAncestor(img, isIElementSection)(state);
        const timeseries = selectAncestor(img, isIElementTimeseries)(state);
        const tsection = selectAncestor(timeseries, isIElementSection)(state);
        return (!isNullPose(img.pose) ||
            section === undefined ||
            !isNullPose(section.pose) ||
            timeseries === undefined ||
            !isNullPose(timeseries.pose) ||
            tsection === undefined ||
            !isNullPose(tsection.pose));
    };
}
/**
 * Selector to pick all the other panos with the same timestamp in the reference area section
 *
 * @param referenceAreaSectionId The area section we want to collect the panos for
 * @param referenceId - The id of the element to use as a reference representing target time period
 * @param filterFn - A selector function which is used for filtering the result as required
 * @returns All the pano at the same timestamp of the reference pano or the active one
 */
function selectSameTimestampPanos(referenceAreaSectionId, referenceId, filterFn) {
    return (state) => {
        if (!referenceId)
            return [];
        const sheet = selectIElement(referenceAreaSectionId)(state);
        const element = selectIElement(referenceId)(state);
        if (!element)
            return [];
        const referenceElement = selectAncestor(element, (el) => isIElementVideoRecording(el) ||
            isIElementSectionDataSession(el) ||
            isIElementSectionWithTypeHint(el, IElementTypeHint.room))(state);
        if (!referenceElement) {
            return [];
        }
        const areaSection = selectAncestor(sheet, (el) => el.type === IElementType.section &&
            el.typeHint === IElementTypeHint.area)(state);
        if (!areaSection) {
            throw new Error("Project without an area section or pano not in a section");
        }
        // Get all the panos for the current area section and timestamp
        const panos = selectClosestPanosInTime(referenceElement.id, areaSection.id)(state);
        return filterFn ? panos.filter((pano) => filterFn(pano)(state)) : panos;
    };
}
/**
 * Compare to array of ielements for equality using the IElement id
 *
 * @param a First array
 * @param b Second array
 * @returns True if they are the same
 */
function areIElementArraysEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; ++i) {
        if (a[i].id !== b[i].id) {
            return false;
        }
    }
    return true;
}
/**
 * @returns the creation date of the section, child of a time series, parent of the passed element
 * @param element The IElement we want to query the time series section creation date of
 */
function selectElementTimeSeriesSectionCreationDate(element) {
    return (state) => {
        const root = selectRootIElement(state);
        if (!root)
            throw Error("Invalid project without a root");
        function predicate(element) {
            if (!isIElementSection(element)) {
                return false;
            }
            if (!element.parentId) {
                return false;
            }
            const parent = selectIElement(element.parentId)(state);
            return parent?.type === IElementType.timeSeries;
        }
        const section = selectAncestor(element, predicate)(state);
        return new Date((section ?? root).createdAt).getTime();
    };
}
/**
 * @returns Current loading state of the project
 * @param state The store
 */
function selectProjectLoadingState(state) {
    return state.iElements.projectLoadingState;
}
/**
 * @returns If the target element id is the root of a tree that is being loaded
 * @param {GUID} subtreeRootId - The id of the element to check
 */
function selectIsSubtreeLoading(subtreeRootId) {
    return (state) => state.iElements.loadingSubTrees.includes(subtreeRootId);
}
/**
 * @returns true if a subtree of the project is loaded
 * @param subtreeRootId id of the root of the subtree to check
 */
function selectIsSubtreeLoaded(subtreeRootId) {
    return (state) => {
        if (!subtreeRootId) {
            return false;
        }
        const node = selectIElement(subtreeRootId)(state);
        if (!node) {
            return false;
        }
        if (!node.childrenIds || node.childrenIds.length === 0) {
            return true;
        }
        return node.childrenIds.every((id) => !!selectIElement(id)(state));
    };
}
/**
 * This selector returns the element where the time information is encoded for another elements in the project
 * Eg. To know the acquisition time of:
 * * an IElementPointCloudStream we need to find the IElementSection(DataSession) that contains it
 * * an IElementSection(OdometricPath) we need to find the IElementSection(VideoRecordings) that contains it
 *
 * @returns The element to use to identify the current moment the passed element was acquired (to make time based decision)
 * @param element The element for which we want to compute the reference
 * @param candidates The list of possible reference elements
 */
function selectReferenceElement(element, candidates) {
    return (state) => {
        const reference = selectAncestor(element, (e) => candidates.some((candidate) => e === candidate))(state);
        if (reference) {
            assert(isIElementSection(reference), "As the reference is one of the candidate it must be section");
            return reference;
        }
        const [preferred, others] = partition(candidates, (el) => isIElementSectionDataSession(el) || isIElementVideoRecording(el));
        const preferredPick = pickClosestInTime(element, preferred);
        if (preferredPick) {
            return preferredPick;
        }
        return pickClosestInTime(element, others);
    };
}
/**
 * @returns The section/data session containing the input element
 * @param element The element for which we want to compute the reference data
 */
function selectDatasetOrArea(element) {
    return (state) => {
        // The returned type could be:
        //      - a data session (Section - DataSession)
        //      - an area (Section - Area)
        const reference = selectAncestor(element, (e) => isIElementSectionDataSession(e) ||
            isIElementGenericDataset(e) ||
            isIElementSectionWithTypeHint(e, IElementTypeHint.area))(state);
        return reference;
    };
}
/**
 * @deprecated Does not consider the area's volume when looking for data.
 * Only direct descendants in the iElement tree and the deprecated version of the volumeQuery.
 * The volume query v2 needs to be used to get the expected set of data sets to show for an area.
 * @returns The list of data sessions in the current area that have 3d data available
 * @param area The input area
 */
function selectAreaDataSessions(area) {
    return (state) => {
        // Account for video recording still in a separate video recording timeseries
        const dataSessions = selectLegacyVideoRecordings(area)(state);
        // Enumerate DataSession from the BI-tree
        // TODO: Remove when all project will be migrated to the capture tree (https://faro01.atlassian.net/browse/SWEB-4468)
        const dataTimeSeries = selectChildDepthFirst(area, isIElementTimeseriesDataSession, 1)(state);
        if (dataTimeSeries) {
            dataSessions.push(...selectIElementChildren(dataTimeSeries.id)(state).filter(isIElementSection));
        }
        // Enumerate DataSets from the capture tree
        const dataSetIds = selectAreaCaptureTreeDataSetIds(state, area.id);
        for (const id of dataSetIds) {
            const section = selectIElement(id)(state);
            if (!section ||
                !isIElementSection(section) ||
                !!selectAncestor(section, isIElementSectionDataSession)(state)) {
                continue;
            }
            dataSessions.push(section);
        }
        return dataSessions;
    };
}
/**
 * @returns what kind of data a data session contains
 * @param session to check
 */
function selectDataSessionContainedData(session, { pointCloudFilter } = {}) {
    return (state) => ({
        panos: !!selectChildDepthFirst(session, isIElementImg360)(state),
        pointClouds: !!selectChildDepthFirst(session, (el) => isIElementPointCloudStream(el) &&
            (pointCloudFilter ? pointCloudFilter(el) : true))(state),
        gaussianSplats: !!selectChildDepthFirst(session, isIElementGaussianSplats)(state),
    });
}
/**
 * @param id of the element to check
 * @returns true if the passed data session element has a DataSetVideoWalk
 */
function selectIsVideoWalkDataSession(id) {
    return (state) => {
        const children = selectIElementChildren(id)(state);
        return children.some(isIElementDataSetVideoWalk);
    };
}
/**
 * @param id of the element to fetch
 * @returns an IElementGenericDataset element or undefined if the given id does not
 * exist or is not an IElementGenericDataset
 */
function selectGenericDatasetElement(id) {
    return (state) => {
        const element = selectIElement(id)(state);
        if (!element)
            return;
        if (isIElementGenericDataset(element)) {
            return element;
        }
    };
}
/**
 * This selector checks if the the data session is empty or not.
 *
 * @param session element to check the children of, to see if its empty
 * @returns true if the data session does not contain any element the sphere viewer can render
 */
function selectIsDataSessionEmpty(session) {
    return (state) => 
    // Check if the session contains any of the data we can show in the viewer
    // return early as soon as the first valid data is found
    // The code can't assume the valid data will be at a specific depth of the data structure
    // as in a focus project the scans could be at any depth
    !selectChildDepthFirst(session, (element) => isIElementGenericPointCloudStream(element) ||
        isIElementImg360(element) ||
        isIElementGenericImgSheet(element) ||
        isIElementGaussianSplats(element))(state);
}
/**
 * @deprecated Existing legacy video recording timeseries have been migrated into data sets.
 * @returns The list of video recordings of an area still defined in a separate VideoRecordings time series
 * @param area The input area
 */
function selectLegacyVideoRecordings(area) {
    return (state) => {
        const videoRecordingTimeSeries = selectChildDepthFirst(area, isIElementVideoRecordingTimeseries, 1)(state);
        if (!videoRecordingTimeSeries) {
            return [];
        }
        return selectIElementChildren(videoRecordingTimeSeries.id)(state)
            .filter(isIElementVideoRecording)
            .filter((videoRecording) => selectIsValidVideoRecording(videoRecording)(state));
    };
}
/**
 * @returns true is the given video recording is a valid one
 * @param videoRecording video recording to be checked for validity
 */
function selectIsValidVideoRecording(videoRecording) {
    return (state) => !!selectChildDepthFirst(videoRecording, isIElementImg360)(state);
}
/**
 * @returns The list of cad models of an area, not belonging to a data session
 * @param area The input area
 */
function selectAreaBimModels(area) {
    return (state) => {
        const cadGroup = selectChildDepthFirst(area, (el) => isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.bimModel), 1)(state);
        if (!cadGroup)
            return [];
        return selectChildrenDepthFirst(cadGroup, isIElementBimModelSection)(state);
    };
}
/**
 * @deprecated Does not consider the area's volume when looking for data.
 * Only searches in direct descendants of the iElement tree.
 * The volume query v2 needs to be used to get the expected set of rooms to show for an area.
 * @returns The list of rooms time series and room sections of an area, not belonging to a data session
 * @param area The input area
 * @param excludeVideoModeClones true to exclude the rooms cloned by video mode trajectories
 */
function selectAreaRoomsData(area, excludeVideoModeClones = true) {
    return (state) => {
        const roomsGroup = selectChildDepthFirst(area, (el) => isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.rooms), 1)(state);
        if (!roomsGroup) {
            return { roomsTimeSeries: [], roomsSections: [], roomsWithImg2d: [] };
        }
        const roomsTimeSeries = selectChildrenDepthFirst(roomsGroup, isIElementTimeseries, 2)(state);
        const roomsSections = roomsTimeSeries
            .flatMap((room) => selectIElementChildren(room.id)(state))
            .filter(isIElementSection)
            .filter((el) => !excludeVideoModeClones || !isIElementVideoModeCopy(el));
        const allRoomsIds = roomsSections.map((section) => section.id);
        const roomsWithImg2d = selectChildrenDepthFirst(roomsGroup, isIElementRoomSection, 1)(state).filter((room) => {
            const pano = selectChildDepthFirst(room, isIElementImg360)(state);
            const photo2d = selectChildDepthFirst(room, isIElementImg2d, 2)(state);
            return !pano && photo2d;
        });
        return {
            // Clean up rooms times series from timeseries containing only videomode clones
            roomsTimeSeries: roomsTimeSeries.filter((ts) => ts.childrenIds?.some((id) => includes(allRoomsIds, id))),
            roomsSections,
            roomsWithImg2d,
        };
    };
}
/**
 * @returns the volumes group for an area
 * @param area the area to get the volumes group for
 */
function selectAreaVolumesGroup(area) {
    return (state) => {
        if (!area) {
            return;
        }
        return selectChildDepthFirst(area, (el) => isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.clippingBox), 1)(state);
    };
}
/**
 * @returns the list of volumes defining the bounding volume of an area.
 * @param area the area to get the volumes for
 */
function selectMainAreaVolume(area) {
    return (state) => {
        const volumesGroup = selectAreaVolumesGroup(area)(state);
        if (!volumesGroup)
            return;
        return selectChildDepthFirst(volumesGroup, isIElementVolume)(state);
    };
}
/**
 * @returns the markup template for "Advanced Markups" (former HoloBuilder markups)
 * @param state current store state
 */
function selectAdvancedMarkupTemplate(state) {
    assert(state.iElements.rootId, "Expected rootId");
    const root = selectRootIElement(state);
    return selectChildDepthFirst(root, (el) => isIElementWithTypeAndHint(el, IElementType.markupTemplate, IElementTypeHint.advancedMarkup))(state);
}
/**
 * @returns the template ids for advanced markups and their fields. Or undefined if the template was not found in the project.
 * @param state current store state
 */
function selectAdvancedMarkupTemplateIds(state) {
    const template = selectAdvancedMarkupTemplate(state);
    const assigneeTemplateField = selectChildDepthFirst(template, isIElementMarkupAssigneeIdTemplate)(state);
    const statusTemplateField = selectChildDepthFirst(template, isIElementMarkupIssueStatusTemplate)(state);
    const dueDateTemplateField = selectChildDepthFirst(template, isIElementMarkupIssueDueDateTemplate)(state);
    if (!template ||
        !assigneeTemplateField ||
        !statusTemplateField ||
        !dueDateTemplateField) {
        return;
    }
    return {
        templateId: template.id,
        assigneeTemplateId: assigneeTemplateField.id,
        statusTemplateId: statusTemplateField.id,
        dueDateTemplateId: dueDateTemplateField.id,
    };
}
/**
 * If a markup is synced externally, it will have an additional sibling specific to the provider. e.g:
 * Model3d
 * L Markup
 * L MarkupBim360
 *
 * @param markup the markup IElement to check
 * @returns the external markup for a given markup IElement, or undefined if the markup is not synced externally.
 */
function selectExternalSyncedMarkupFor(markup) {
    return (state) => {
        if (!markup.parentId)
            return;
        return selectIElementChildren(markup.parentId)(state).find(isIElementExternalMarkup);
    };
}
/**
 * @returns whether project viewers are allowed to view markups (as per the HoloBuilder project setting).
 * Or undefined if the permission is not defined.
 * @param state current store state
 */
function selectMarkupsDisplayForViewers(state) {
    const root = selectRootIElement(state);
    if (!root)
        return;
    return root.metaDataMap?.slideContainerMarkupSettings?.displayForViewers;
}
/* eslint-disable jsdoc/require-param */
/**
 * @returns an array with all the IDs of the datasets inside the area
 * @param state application state
 * @param areaId the id of the area to query the datasets of
 */
const selectAreaCaptureTreeDataSetIds = createSelector([
    (state) => state.iElements.areaDataSets,
    (_, areaId) => areaId,
], (areaDataSets, areaId) => {
    if (!areaId)
        return [];
    return areaDataSets[areaId]?.map((dataSet) => dataSet.elementId) ?? [];
});
/* eslint-enable jsdoc/require-param */
/* eslint-disable jsdoc/require-param */
/**
 * @returns an array with all the IDs of areas that contain a capture tree data-set.
 * @param state application state
 * @param dataSetId the id of the dataset to query the area for
 */
const selectAreasForCaptureTreeDataSet = createSelector([
    (state) => state.iElements.areaDataSets,
    (_, dataSetId) => dataSetId,
], (areaDataSets, dataSetId) => {
    if (!dataSetId)
        return [];
    return Object.entries(areaDataSets)
        .filter(([, dataSets]) => dataSets?.find((ds) => ds.elementId === dataSetId))
        .map(([areaId]) => areaId);
});
/* eslint-enable jsdoc/require-param */
/**
 * @param element reference element to start the search. It should be a panorama image.
 * @param predicate function used to filter the elements
 * @returns the IElement of the sibling pano, or undefined if there is none
 */
function selectSiblingPano(element, predicate) {
    return (state) => {
        if (!element)
            return;
        const parentSection = selectAncestor(element, isIElementSection)(state);
        let siblingPanos = selectChildrenDepthFirst(parentSection, isIElementImg360, 1)(state);
        if (predicate) {
            siblingPanos = siblingPanos.filter(predicate);
        }
        return siblingPanos.find((pano) => pano.id !== element.id);
    };
}
/**
 * @returns The local pose of an IElement separated into its raw original components read from the Project API.
 * This is particularly useful when writing pose data to the Project API and converting from the Viewer CS to the Project API CS
 * @param iElement The element whose pose we want to compute
 * @param pose The new local pose in the viewer reference system
 * @param extraElements Optional elements that are not yet in the app state, for which we need to compute the world transform
 */
function selectIElementProjectApiLocalPose(iElement, pose, extraElements) {
    return (state) => {
        const extraCache = extraElements
            ? extraElements.reduce((prev, current) => {
                prev[current.id] = current;
                return prev;
            }, {})
            : {};
        const iElements = {
            ...state.iElements.iElements,
            ...extraCache,
        };
        validateExtraElements(iElements, iElement);
        const worldTransform = getIElementWorldTransform(iElements, iElement, state.iElements.worldTransformCache, state.iElements.areaDataSets);
        const parentElement = iElement.parentId
            ? iElements[iElement.parentId]
            : undefined;
        const parentWorldTransform = parentElement
            ? getIElementWorldTransform(iElements, parentElement, state.iElements.worldTransformCache, state.iElements.areaDataSets)
            : DEFAULT_TRANSFORM;
        const isInCaptureTree = isInsideCaptureTree(iElement, iElements);
        return extractProjectApiLocalPose(iElement, pose, isInCaptureTree, worldTransform, parentWorldTransform);
    };
}
/**
 * Validates that the extraElements are passed correctly for the pose calculation.
 * All extra elements must have a valid and existing parent, while not adding more root elements.
 *
 * @param iElementsCombined The dictionary of iElements combined with the extraElements
 * @param elementToAdd The new element to add
 */
function validateExtraElements(iElementsCombined, elementToAdd) {
    const roots = Object.values(iElementsCombined).filter((el) => isValid(el) && !el.parentId);
    // This error can occur, if an extraElement is added without a parentId.
    assert(roots.length === 1, `Expected exactly one root in the combined elements, but found ${roots.length}.`);
    let nextParentId = elementToAdd.parentId;
    assert(nextParentId, "Expected parentId to be defined on the element to add");
    while (nextParentId) {
        const parentElement = iElementsCombined[nextParentId];
        // This error can occur, if an extraElement has been forgotten.
        assert(parentElement, `Missing element for complete pose calculation: ${nextParentId}`);
        nextParentId = parentElement.parentId;
    }
}
/**
 * @returns what kind of fetching is needed for an array of element ids.
 * Distinguishes between:
 * - elements that need fetching themselves
 * - elements that do not have all their ancestors loaded
 * - elements that do not have their children loaded
 * Elements will only appear in the first list in the order above.
 * @param state current app state
 * @param ids the ids of the elements to check
 */
const selectIElementsIsFetchingNeeded = createSelector([
    (state) => state.iElements.iElements,
    (state, ids) => ids,
], (iElementsMap, ids) => {
    const elementsThatNeedFetching = [];
    const elementsThatNeedParentFetching = [];
    const elementsThatNeedChildFetching = [];
    idLoop: for (const id of ids) {
        const element = iElementsMap[id];
        if (!element) {
            elementsThatNeedFetching.push(id);
            continue;
        }
        let parent = element;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        while (parent) {
            const nextParentId = parent.parentId;
            if (!nextParentId) {
                // if we reach the root, the element ancestors are fully loaded. Continue with child loaded check
                break;
            }
            const nextParent = iElementsMap[nextParentId];
            if (!nextParent) {
                elementsThatNeedParentFetching.push(id);
                continue idLoop;
            }
            parent = nextParent;
        }
        const childrenToCheck = element.childrenIds?.slice() ?? [];
        while (childrenToCheck.length > 0) {
            const childId = childrenToCheck.pop();
            const childElement = childId ? iElementsMap[childId] : undefined;
            if (!childElement) {
                elementsThatNeedChildFetching.push(id);
                continue idLoop;
            }
            if (childElement.childrenIds) {
                childrenToCheck.push(...childElement.childrenIds);
            }
        }
    }
    return {
        elementsThatNeedFetching,
        elementsThatNeedParentFetching,
        elementsThatNeedChildFetching,
    };
});
/**
 * @returns A list of IDs corresponding to elements whose descendants are not yet present in the store.
 * @param state current app state
 * @param iElements the elements to check
 */
const selectIElementsIsDescendantFetchingNeeded = createSelector([
    (state) => state.iElements.iElements,
    (state, iElements) => iElements,
], (iElementsMap, iElements) => {
    function hasMissingDescendant(iElement) {
        if (!iElement.childrenIds) {
            return false;
        }
        return iElement.childrenIds.some((childId) => {
            const child = iElementsMap[childId];
            if (!child) {
                return true;
            }
            return hasMissingDescendant(child);
        });
    }
    const elementsWithMissingDescendants = [];
    for (const iElement of iElements) {
        if (hasMissingDescendant(iElement)) {
            elementsWithMissingDescendants.push(iElement.id);
        }
    }
    return elementsWithMissingDescendants;
});
/**
 * @returns the IElementCamView that describes the best view to look at an element. Undefined, if not available.
 * @param element the element the get the view for
 */
function selectBestViewForElement(element) {
    return (state) => {
        const annotationParent = selectAncestor(element, isIElementGenericAnnotation)(state);
        if (annotationParent) {
            return selectBestViewForAnnotation(annotationParent)(state);
        }
    };
}
/**
 * @param annotation the annotation the get the view for
 * @returns the IElementCamView that describes the best view to see an annotation. Might be undefined, if no view was saved.
 */
function selectBestViewForAnnotation(annotation) {
    return (state) => selectChildDepthFirst(annotation, isIElementCamView)(state);
}

// TODO: Re enable console warnings when we move to a proper logging system: https://faro01.atlassian.net/browse/SWEB-459
/* eslint-disable no-console */
/**
 * Fetch some of the IElements of the project
 * Optionally set an element as the root of the tree for loading indication
 */
const fetchProjectIElements = createAsyncThunk("ielements/fetchProjectIElements", async ({ fetcher, loadingIds }, { dispatch, rejectWithValue }) => {
    loadingIds?.map((id) => dispatch(addToLoadingRoots(id)));
    let elements = [];
    try {
        elements = await fetcher().finally(() => {
            loadingIds?.map((id) => dispatch(removeFromLoadingRoots(id)));
        });
    }
    catch (error) {
        return rejectWithValue(error);
    }
    if (!elements.length)
        return elements;
    const root = elements.find((el) => el.id === elements[0].rootId);
    dispatch(addIElements(elements));
    if (root) {
        dispatch(setRootId(root.id));
    }
    return elements;
});
const IELEMENTS_SLICE_INITIAL_STATE = Object.freeze({
    projectId: undefined,
    projectName: undefined,
    projectAccessLevel: undefined,
    companyId: undefined,
    dashboardUrl: undefined,
    iElements: {},
    worldTransformCache: {},
    rootId: undefined,
    loadingSubTrees: [],
    projectLoadingState: undefined,
    tree: [],
    shouldFilterProjectTree: true,
    areaDataSets: {},
});
const iElementsSlice = createSlice({
    name: "ielements",
    initialState: IELEMENTS_SLICE_INITIAL_STATE,
    reducers: {
        /**
         * Initialize the project in the store, compute the root and initialize the world position caches
         *
         * @param state The slice state
         * @param action ID of the project
         */
        initializeProject(state, action) {
            if (state.projectId !== action.payload) {
                state.iElements = {};
                state.worldTransformCache = {};
                state.rootId = undefined;
            }
            state.projectId = action.payload;
        },
        /**
         * Update the company ID to identify what company contains the current project
         *
         * @param state The slice state
         * @param action The id of the company that contains the current project
         */
        setCompanyId(state, action) {
            state.companyId = action.payload;
        },
        /**
         * Update the name of the current project
         *
         * @param state The slice state
         * @param action The name of the current project
         */
        setProjectName(state, action) {
            state.projectName = action.payload;
        },
        /**
         * Update the access level of the current project
         *
         * @param state The slice state
         * @param action The access level of the current project
         */
        setProjectAccessLevel(state, action) {
            state.projectAccessLevel = action.payload;
        },
        /**
         * Update the url to open the dashboard for the current project
         *
         * @param state The slice state
         * @param action The url for the current project dashboard
         */
        setDashboardUrl(state, action) {
            state.dashboardUrl = action.payload;
        },
        /**
         * Add the iElements to the store
         *
         * @param state The slice state
         * @param action The list of IElements
         * @deprecated Use {@link initializeProject} instead to make sure the rootId and the cache is synchronized
         */
        addIElements(state, action) {
            const elements = action.payload;
            const firstElement = elements.at(0);
            if (!firstElement)
                return;
            for (const element of elements) {
                state.iElements[element.id] = element;
            }
            // As this will not re-compute existing values in cache
            // when we add multiple elements it's easier to update the entire cache
            updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, state.rootId);
        },
        /**
         * Set the root id for the project
         *
         * @param state The slice state
         * @param action The new root id
         * @deprecated Use {@link initializeProject} instead to make sure the rootId and the cache is synchronized
         */
        setRootId(state, action) {
            state.rootId = action.payload;
        },
        /**
         * Add an id to the list of roots that are being loaded.
         *
         * @param state - The slice state
         * @param {PayloadAction} action - The id of the element that is being loaded
         */
        addToLoadingRoots(state, action) {
            state.loadingSubTrees.push(action.payload);
        },
        /**
         * Remove an id from the list of roots that are being loaded.
         *
         * @param state - The slice state
         * @param {PayloadAction} action - The id of the element that is done being loaded
         */
        removeFromLoadingRoots(state, action) {
            const index = state.loadingSubTrees.indexOf(action.payload);
            if (index === -1)
                return;
            state.loadingSubTrees.splice(index, 1);
        },
        /**
         * Remove an element and all its children recursively
         *
         * @param state Current state
         * @param action ID of the IElement to remove
         */
        removeIElement(state, action) {
            const iElement = state.iElements[action.payload];
            if (!iElement) {
                return;
            }
            const deletedElements = new Set(iElement.id);
            // remove reference of this element from its parent
            const parent = state.iElements[iElement.parentId ?? ""];
            if (parent) {
                parent.childrenIds =
                    parent.childrenIds?.filter((x) => x !== iElement.id) ?? null;
            }
            // Remove this element and all its children from the store
            function recursiveRemoveIElement(id) {
                const el = state.iElements[id];
                if (!el)
                    return;
                const children = el.childrenIds ?? [];
                for (const child of children) {
                    recursiveRemoveIElement(child);
                }
                deletedElements.add(id);
                delete state.iElements[id];
                delete state.worldTransformCache[id];
            }
            recursiveRemoveIElement(action.payload);
            // Remove elements which link to any of the deleted elements
            const linkingElements = [];
            for (const [id, el] of Object.entries(state.iElements)) {
                if (!el)
                    continue;
                if (isIElemLink(el) && deletedElements.has(el.target_Id)) {
                    // Don't delete the element directly here to avoid modifying the object we are iterating over
                    linkingElements.push(id);
                }
            }
            for (const id of linkingElements) {
                delete state.iElements[id];
                delete state.worldTransformCache[id];
            }
        },
        changePosition(state, { payload }) {
            const { id, pose } = payload;
            const iElement = state.iElements[id];
            if (iElement) {
                iElement.pose = pose;
                updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, id);
            }
            else {
                console.warn(`IElement with id ${id} not found`);
            }
        },
        setGeoReferenced(state, action) {
            const iElementId = action.payload;
            const iElement = state.iElements[iElementId];
            // iElement might be undefined in case of `iElementId` being invalid
            if (iElement) {
                iElement.pose = {
                    isWorldPose: true,
                    isWorldRot: true,
                    isWorldScale: true,
                    pos: null,
                    rot: null,
                    scale: null,
                    gps: null,
                };
                updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, iElementId);
            }
            else {
                console.warn(`IElement with id ${iElementId} not found`);
            }
        },
        changeName(state, { payload }) {
            const { id, name } = payload;
            const iElement = state.iElements[id];
            if (iElement) {
                iElement.name = name;
            }
            else {
                console.warn(`IElement with id ${id} not found`);
            }
        },
        changeDescr(state, { payload }) {
            const { id, descr } = payload;
            const iElement = state.iElements[id];
            if (iElement) {
                iElement.descr = descr;
            }
            else {
                console.warn(`IElement with id ${id} not found`);
            }
        },
        addChild(state, { payload }) {
            const { id, childId } = payload;
            const iElement = state.iElements[id];
            if (iElement) {
                iElement.childrenIds = [...(iElement.childrenIds ?? []), childId];
                updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, id);
            }
            else {
                console.warn(`IElement with id ${id} not found`);
            }
        },
        moveIElement(state, { payload, }) {
            const { id, oldParentId, newParentId, moveIndexInTargetParent = -1, } = payload;
            const iElement = state.iElements[id];
            const oldParent = state.iElements[oldParentId];
            const newParent = state.iElements[newParentId];
            // iElement might be undefined in case of `id` being invalid
            if (!iElement) {
                console.warn(`IElement with id ${id} not found`);
                return;
            }
            // oldParent might be undefined in case of `oldParentId` being invalid
            if (!oldParent) {
                console.warn(`IElement with id ${oldParentId} not found`);
                return;
            }
            // newParent might be undefined in case of `newParentId` being invalid
            if (!newParent) {
                console.warn(`IElement with id ${newParentId} not found`);
                return;
            }
            oldParent.childrenIds = oldParent.childrenIds
                ? oldParent.childrenIds.filter((existingId) => existingId !== id)
                : [];
            // FIXME: https://faro01.atlassian.net/browse/SWEB-506
            if (moveIndexInTargetParent) {
                newParent.childrenIds?.splice(moveIndexInTargetParent, 0, id);
            }
            else {
                newParent.childrenIds = newParent.childrenIds
                    ? [...newParent.childrenIds, id]
                    : [id];
            }
            iElement.parentId = newParent.id;
            // Update the world positions of the moved subtree
            updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, id);
        },
        changeIndexInParent(state, { payload, }) {
            const { id, childToMove, moveIndexInTargetParent } = payload;
            const parent = state.iElements[id];
            // oldParent might be undefined in case of `id` being invalid
            if (!parent) {
                console.warn(`IElement with id ${id} not found`);
                return;
            }
            const fromIndex = parent.childrenIds?.findIndex((existingId) => existingId === childToMove);
            if (!fromIndex) {
                console.warn(`childToMove with id ${childToMove} not found`);
                return;
            }
            // Removes element from `fromIndex` and inserts that element into `moveIndexInTargetParent` position
            parent.childrenIds?.splice(moveIndexInTargetParent, 0, ...parent.childrenIds.splice(fromIndex, 1));
        },
        /**
         * Update the world transform cache for a sub-tree of the project, starting from an element
         * evaluating all the children
         *
         * @param state The slice state
         * @param param1 The Action payload
         * @param param1.payload The ID of the element to start the sub-tree update
         */
        updateWorldTransformCache(state, { payload }) {
            updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, payload);
        },
        /**
         * Add to the store the datasets for a specific area, replacing the info if already present for that area
         *
         * @param state the slice state
         */
        addAreaDataSets(state, { payload, }) {
            state.areaDataSets = {
                ...state.areaDataSets,
                [payload.areaId]: payload.dataSets,
            };
            if (state.rootId) {
                updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, state.rootId);
            }
        },
        /**
         * Set to the store the mapping between areas and dataset, replacing the entire previous mapping if present
         *
         * @param state the slice state
         */
        setAreaDataSets(state, { payload }) {
            state.areaDataSets = payload;
            if (state.rootId) {
                updateWorldTransformCacheSubTree(state.iElements, state.worldTransformCache, state.areaDataSets, state.rootId);
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearStore, () => IELEMENTS_SLICE_INITIAL_STATE);
        // Handling the state of the fetchProjectIElements thunk
        builder.addCase(fetchProjectIElements.pending, (state) => {
            state.projectLoadingState = "loading";
        });
        builder.addCase(fetchProjectIElements.fulfilled, (state) => {
            state.projectLoadingState = "done";
        });
        builder.addCase(fetchProjectIElements.rejected, (state) => {
            state.projectLoadingState = "failed";
        });
    },
});
const { initializeProject, setCompanyId, setProjectName, setProjectAccessLevel, setDashboardUrl, addIElements, removeIElement, setRootId, changePosition, setGeoReferenced, changeName, changeDescr, addChild, moveIElement, changeIndexInParent, updateWorldTransformCache, addToLoadingRoots, removeFromLoadingRoots, setAreaDataSets, addAreaDataSets, } = iElementsSlice.actions;
const iElementsReducer = iElementsSlice.reducer;

/**
 * @param dashboardCompanyUrl url to the company page in the sphere dashboard (Eg: https://sphere.holobuilder.com/companyId)
 * @returns the dashboard url for the active projects list for the company
 *
 * The core api backends returns the dashboardCompanyUrl to use in the project metadata
 */
function getActiveProjectsPage(dashboardCompanyUrl) {
    return `${dashboardCompanyUrl}/projects/active`;
}
/**
 * @param dashboardCompanyUrl url to the company page in the sphere dashboard (Eg: https://sphere.holobuilder.com/companyId)
 * @param projectId of the project to compute the url for
 * @returns the dashboard url for the active projects list for the company
 *
 * The core api backends returns the dashboardCompanyUrl to use in the project metadata
 */
function getProjectDetailsPage(dashboardCompanyUrl, projectId) {
    return `${dashboardCompanyUrl}/projects/${projectId}/details`;
}
/**
 * @deprecated Use {@link getDataManagementPage} instead.
 * @returns the dashboard url to open the page showing the list of raw data for data preparation
 * @param dashboardCompanyUrl url to the company page in the sphere dashboard (Eg: https://sphere.holobuilder.com/companyId)
 * @param projectId of the project to compute the url for
 */
function getProjectRawDataPage(dashboardCompanyUrl, projectId) {
    // TODO: Remove &enableBeta flag https://faro01.atlassian.net/browse/NRT-1303
    return `${dashboardCompanyUrl}/projects/${projectId}/data-management?type=rawData&enableBeta=true`;
}
/**
 * @deprecated Use {@link getDataManagementPage} instead.
 * @returns the dashboard url to open the page showing the list of prepared data
 * @param dashboardCompanyUrl url to the company page in the sphere dashboard (Eg: https://sphere.holobuilder.com/companyId)
 * @param projectId of the project to compute the url for
 */
function getProjectPreparedDataPage(dashboardCompanyUrl, projectId) {
    // TODO: Remove &enableBeta flag https://faro01.atlassian.net/browse/NRT-1303
    return `${dashboardCompanyUrl}/projects/${projectId}/data-management?type=preparedData&enableBeta=true`;
}
/**
 * @returns the dashboard url to open the data management page
 * @param dashboardCompanyUrl url to the company page in the sphere dashboard (Eg: https://sphere.holobuilder.com/companyId)
 * @param projectId of the project to compute the url for
 */
function getDataManagementPage(dashboardCompanyUrl, projectId) {
    return `${dashboardCompanyUrl}/projects/${projectId}/data-management`;
}

/**
 * Ordering function to sort any elements with createdAt property from newest to oldest
 *
 * @param a first element
 * @param b second element
 * @returns a negative value if the a is newer than b, zero if they were created the same moment, and a positive value otherwise
 */
function newestToOldest(a, b) {
    return compareDateTimes(b.createdAt, a.createdAt);
}

/**
 * List of known Point Cloud types.
 */
var PointCloudType;
(function (PointCloudType) {
    /** A higher quality, stationary scan of a mobile device. */
    PointCloudType["flash"] = "flash";
    /** A non-stationary scan, generally with lower quality. */
    PointCloudType["mobile"] = "mobile";
    /** A merged and post-processed point cloud generated from individual scans */
    PointCloudType["project"] = "project";
    /** A point cloud for an individual scan */
    PointCloudType["singleScan"] = "singleScan";
    /** General point cloud scan. */
    PointCloudType["other"] = "other";
})(PointCloudType || (PointCloudType = {}));
/** Type hints used to mark sections containing data of an individual scan */
const SINGLE_SCAN_TYPE_HINTS = [
    /** Scan produced by a FARO Focus device */
    IElementTypeHint.focusScan,
    /** Scan produced by a FARO ELS/Blink device */
    IElementTypeHint.elsScan,
    /** Generic (third-party) scan with a pano */
    IElementTypeHint.structuredScan,
    /** Generic (third-party) scan */
    IElementTypeHint.unstructuredScan,
    /** WebShare migration scan */
    IElementTypeHint.scanWs,
    /** Mobile scan produced by a FARO Orbis device */
    IElementTypeHint.orbisScan,
    /** Flash scan produced by a FARO Orbis device */
    IElementTypeHint.orbisFlashScan,
    /** Scan produced by SiteScape */
    IElementTypeHint.siteScapeScan,
];
/**
 * @param pointCloud The point cloud to determine the type of.
 * @returns The type of scan technique used to create the point cloud.
 */
function selectPointCloudType(pointCloud) {
    return (state) => {
        const isFlash = !!selectAncestor(pointCloud, (el) => isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.flash))(state);
        // ASSUMPTION: GeoSlam datasets contain either flash scans or the mobile scans
        const isMobile = !isFlash &&
            !!selectAncestor(pointCloud, (el) => isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetGeoSlam))(state);
        const isSingleScan = !!selectAncestor(pointCloud, isSingleScanSection)(state);
        const isProjectPointCloud = !!selectAncestor(pointCloud, (el) => isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetFocus) ||
            isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetEls) ||
            isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetOrbis) ||
            isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetSiteScape) ||
            isIElementWithTypeAndHint(el, IElementType.section, IElementTypeHint.dataSetCapture))(state);
        if (isFlash) {
            return PointCloudType.flash;
        }
        if (isMobile) {
            return PointCloudType.mobile;
        }
        if (isSingleScan) {
            return PointCloudType.singleScan;
        }
        if (isProjectPointCloud) {
            return PointCloudType.project;
        }
        return PointCloudType.other;
    };
}
/**
 * @returns true if an element is the section of a single scan
 * @param el The element to check
 */
function isSingleScanSection(el) {
    return isIElementWithTypeAndHints(el, IElementType.section, SINGLE_SCAN_TYPE_HINTS);
}

export { DEFAULT_TRANSFORM, IELEMENTS_SLICE_INITIAL_STATE, LEFT_TO_RIGHT, PointCloudType, Z_TO_Y_UP_MATRIX, Z_TO_Y_UP_QUAT, addAreaDataSets, addChild, addIElements, addToLoadingRoots, areIElementArraysEqual, changeDescr, changeIndexInParent, changeName, changePosition, clearStore, computeCachedWorldTransform, computeReferenceSystemProperties, computeReferenceSystemTransform, convertIElementToThreePosition, convertIElementToThreeRotation, convertIElementToThreeTransform, convertReferenceSystemProperties, convertThreeToIElementTransform, createSignedUrlForIElementThumbnailUri, createSignedUrlForIElementUri, createSignedUrlsForIElementLevelsOfDetail, createSignedUrlsForIElementSources, decomposeMatrix, extractProjectApiLocalPose, fetchProjectIElements, getActiveProjectsPage, getDataManagementPage, getElementTransformation, getIElementWorldTransform, getProjectDetailsPage, getProjectPreparedDataPage, getProjectRawDataPage, iElementsReducer, initializeProject, isIElementVideoModeCopy, isInsideCaptureTree, isPoseLeftHanded, isSingleScanSection, moveIElement, newestToOldest, poseToMatrix4, removeFromLoadingRoots, removeIElement, requestSignedUrlForIElement, selectActiveTree, selectAdvancedMarkupTemplate, selectAdvancedMarkupTemplateIds, selectAllAreaDatasets, selectAllIElements, selectAllIElementsOfType, selectAltitudeDifferenceToFloor, selectAncestor, selectAreAllSelectedIdsPanoramas, selectAreaBimModels, selectAreaCaptureTreeDataSetIds, selectAreaDataSessions, selectAreaRoomsData, selectAreaVolumesGroup, selectAreasForCaptureTreeDataSet, selectBestViewForAnnotation, selectBestViewForElement, selectChildDepthFirst, selectChildrenDepthFirst, selectClosestIElementInTime, selectClosestPanosInTime, selectCompanyId, selectDashboardUrl, selectDataSessionContainedData, selectDatasetOrArea, selectElementTimeSeriesSectionCreationDate, selectExternalSyncedMarkupFor, selectGenericDatasetElement, selectHasValidPose, selectHaveElementsAccurateWorldScale, selectIElement, selectIElementAncestors, selectIElementChildren, selectIElementPanoramaElement, selectIElementPanoramaSection, selectIElementProjectApiLocalPose, selectIElementTimeseries, selectIElementType, selectIElementWorldMatrix, selectIElementWorldPosition, selectIElementWorldTransform, selectIElements, selectIElementsIsDescendantFetchingNeeded, selectIElementsIsFetchingNeeded, selectIElementsWorldPosition, selectIElementsWorldTransform, selectIsChildOfRooms, selectIsDataSessionAligned, selectIsDataSessionEmpty, selectIsElementAligned, selectIsFloorWithoutScale, selectIsIElementAncestorOf, selectIsLocationMarkerSection, selectIsPointCloudAligned, selectIsSubtreeLoaded, selectIsSubtreeLoading, selectIsValidVideoRecording, selectIsVideoWalkDataSession, selectLegacyVideoRecordings, selectLoadedIElements, selectMainAreaVolume, selectMarkupsDisplayForViewers, selectNonPano3dModels, selectNumberOfPointCloudsOnFloor, selectPointCloudType, selectProjectAccessLevel, selectProjectId, selectProjectLoadingState, selectProjectName, selectRecentChild, selectReferenceElement, selectRootForNewCAD, selectRootForNewPointCloud, selectRootIElement, selectRootId, selectSameTimestampPanos, selectSiblingPano, selectTimeseriesSection, setAreaDataSets, setCompanyId, setDashboardUrl, setGeoReferenced, setProjectAccessLevel, setProjectName, setRootId, updateWorldTransformCache, updateWorldTransformCacheSubTree, useKeepSignedUrlsAlive, useSignedUrl, useValidSignedUrl, useValidSignedUrls, useValidUrl };
